import React, { useEffect, useState, useRef } from "react";
import { Shield, Plus, AlertTriangle, Sparkles, ScrollText, Users, Gauge, Globe, UserCog, Trash2, X, Upload, Landmark, Handshake, DollarSign, Copy, Check, ListChecks, Pencil, Ban, RotateCcw, Radio, Brain, BookCheck, Megaphone, Gift, Package, Truck, Zap, Clock, Save, LayoutTemplate, MessageSquare } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { AdminShell } from "@/components/AdminSidebar";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import DateTimePicker from "@/components/DateTimePicker";
import dayjs from "dayjs";
import { ContestToolbar, StatusBadge, badgeOf, relTime, VIEW_OF } from "@/components/admin/ContestToolbar";
import { HoldReasons, BulkBar } from "@/components/admin/ContestBulk";
import SmsReminders from "@/components/admin/SmsReminders";
import PrizeHolds from "@/components/admin/PrizeHolds";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SPECIALS = "!@#$%&*?-_/";
// Track types drive which characters a custom keyboard may use. www. and .com are always buttons.
const TRACK_TYPES = {
  alphabetical: { label: "alphabetical", example: "a A b B c C …", re: /^[A-Za-z]+$/ },
  numerical: { label: "numerical", example: "1 2 3 4 5 …", re: /^[0-9]+$/ },
  alphanumeric: { label: "alphanumeric", example: "1a 2b 3C 4d 5E …", re: /^[A-Za-z0-9]+$/ },
  special: {
    label: "special characters", example: "% a b1 # 3 & …",
    re: new RegExp("^[A-Za-z0-9" + SPECIALS.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&") + "]+$"),
    requireSpecial: true,
  },
};

// Hard input filter: strip any character not allowed by the track type so you
// can NEVER type a letter on a numerical track (etc.). Mirrors TRACK_TYPES.re.
const _specialEsc = SPECIALS.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");
const TRACK_DISALLOWED = {
  alphabetical: /[^A-Za-z]/g,
  numerical: /[^0-9]/g,
  alphanumeric: /[^A-Za-z0-9]/g,
  special: new RegExp("[^A-Za-z0-9" + _specialEsc + "]", "g"),
};
const sanitizeForTrack = (v, trackKey) =>
  (v || "").replace(TRACK_DISALLOWED[trackKey] || TRACK_DISALLOWED.alphabetical, "");
// Alphanumeric tracks must contain BOTH at least one letter and one number.
const alnumHasBoth = (toks) => {
  const j = (toks || []).join("");
  return /[A-Za-z]/.test(j) && /[0-9]/.test(j);
};

const TABS = [
  ["metrics", "Metrics", Gauge],
  ["create", "Create Contest", Plus],
  ["contests", "Manage Contests", ListChecks],
  ["review", "Review Queue", AlertTriangle],
  ["content", "Content Engine", Sparkles],
  ["audit", "Audit Log", ScrollText],
  ["users", "Users", Users],
  ["team", "Team", UserCog],
  ["4thave", "4th AVE", Landmark],
  ["affiliates", "Racing Teams", Handshake],
  ["payments", "Prize Funding", DollarSign],
  ["fulfillment", "Prize Fulfillment", Gift],
  ["regions", "Restricted Regions", Globe],
  ["sms", "SMS Reminders", MessageSquare],
];

function PrizeFulfillment() {
  const [data, setData] = useState({ prizes: [], counts: {} });
  const [filter, setFilter] = useState("ready_to_fulfill");
  const [inputs, setInputs] = useState({});
  const [busy, setBusy] = useState(null);

  const load = () => api.get(`/admin/prizes${filter ? `?status=${filter}` : ""}`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const fulfill = async (p) => {
    const v = inputs[p.award_id] || {};
    setBusy(p.award_id);
    try {
      const payload = p.prize_type === "gift_card"
        ? { gift_card_code: v.code }
        : { tracking_number: v.tracking, carrier: v.carrier };
      await api.post(`/admin/prizes/${p.award_id}/fulfill`, payload);
      toast.success("Prize fulfilled");
      setInputs((s) => ({ ...s, [p.award_id]: {} }));
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const setIn = (id, k, val) => setInputs((s) => ({ ...s, [id]: { ...(s[id] || {}), [k]: val } }));
  const c = data.counts || {};

  return (
    <div data-testid="prize-fulfillment">
      <h2 className="font-heading text-3xl tracking-tight mb-1 flex items-center gap-2"><Gift className="text-race-red" /> Prize Fulfillment</h2>
      <p className="text-race-sub text-sm mb-4">Gift-card and physical-item prizes claimed by winners (identity-verified). Enter a gift-card code or shipment tracking to fulfill.</p>
      <div className="flex flex-wrap gap-2 mb-4">
        {[["ready_to_fulfill", "To Fulfill"], ["pending_claim", "Awaiting Claim"], ["shipped", "Shipped"], ["redeemed", "Redeemed"], ["", "All"]].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} data-testid={`pf-filter-${v || "all"}`}
            className={`text-xs font-mono uppercase tracking-wide rounded-full px-3 py-1.5 border ${filter === v ? "bg-race-red text-race-bg border-race-red" : "border-race-line text-race-sub"}`}>
            {l} {c[v] != null ? `(${c[v]})` : ""}
          </button>
        ))}
      </div>
      {data.prizes.length === 0 ? (
        <p className="text-race-sub text-sm" data-testid="pf-empty">No prizes in this view.</p>
      ) : (
        <div className="space-y-3">
          {data.prizes.map((p) => (
            <div key={p.award_id} data-testid={`pf-award-${p.award_id}`} className="bg-race-surface border border-race-line rounded-sm p-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  {p.image_url ? (
                    <img src={`${BACKEND}${p.image_url}`} alt={p.description} data-testid={`pf-image-${p.award_id}`}
                      className="h-10 w-10 object-cover rounded-sm border border-race-line" />
                  ) : p.prize_type === "gift_card" ? <Gift size={16} className="text-race-volt" /> : <Package size={16} className="text-race-volt" />}
                  <span className="font-medium text-race-txt">{p.description}</span>
                  <span className="text-xs text-race-sub">${(p.fmv_usd || 0).toLocaleString()} FMV</span>
                </div>
                <span className="text-[10px] uppercase font-mono border border-race-line rounded-full px-2 py-0.5 text-race-sub">{p.status.replace(/_/g, " ")}</span>
              </div>
              <div className="text-xs text-race-sub mt-1">
                Winner: <span className="text-race-txt">{p.user_name}</span> ({p.user_email}) · {p.contest_name}
                {p.kyc_verified && <span className="text-green-400 ml-2">ID ✓</span>}
                {p.w9_submitted && <span className="text-sky-400 ml-2">W-9 ✓</span>}
              </div>
              {p.shipping && (
                <div className="text-xs text-race-sub mt-2 bg-race-bg border border-race-line rounded-sm p-2" data-testid={`pf-ship-${p.award_id}`}>
                  <div className="overline text-[9px] mb-0.5">Ship to</div>
                  {p.shipping.name}, {p.shipping.line1}{p.shipping.line2 ? `, ${p.shipping.line2}` : ""}, {p.shipping.city}, {p.shipping.state} {p.shipping.postal_code}, {p.shipping.country}
                </div>
              )}
              {p.status === "ready_to_fulfill" && (
                <div className="flex flex-wrap gap-2 mt-3 items-center">
                  {p.prize_type === "gift_card" ? (
                    <input className={inp + " flex-1 min-w-[200px]"} placeholder="Gift-card code / redemption link"
                      value={(inputs[p.award_id] || {}).code || ""} onChange={(e) => setIn(p.award_id, "code", e.target.value)} data-testid={`pf-code-${p.award_id}`} />
                  ) : (
                    <>
                      <input className={inp + " flex-1 min-w-[140px]"} placeholder="Carrier (optional)"
                        value={(inputs[p.award_id] || {}).carrier || ""} onChange={(e) => setIn(p.award_id, "carrier", e.target.value)} data-testid={`pf-carrier-${p.award_id}`} />
                      <input className={inp + " flex-1 min-w-[160px]"} placeholder="Tracking number (optional)"
                        value={(inputs[p.award_id] || {}).tracking || ""} onChange={(e) => setIn(p.award_id, "tracking", e.target.value)} data-testid={`pf-tracking-${p.award_id}`} />
                    </>
                  )}
                  <button onClick={() => fulfill(p)} disabled={busy === p.award_id} data-testid={`pf-fulfill-${p.award_id}`}
                    className="flex items-center gap-1.5 bg-race-red hover:bg-race-redhover text-race-bg font-mono text-xs uppercase tracking-wide px-4 py-2.5 rounded-sm transition-colors disabled:opacity-50">
                    <Truck size={14} /> {busy === p.award_id ? "…" : p.prize_type === "gift_card" ? "Send Code" : "Mark Shipped"}
                  </button>
                </div>
              )}
              {p.gift_card_code && <div className="text-xs text-green-400 mt-2 font-mono" data-testid={`pf-issued-${p.award_id}`}>Code issued: {p.gift_card_code}</div>}
              {p.tracking_number && <div className="text-xs text-green-400 mt-2 font-mono">Tracking: {p.tracking_number}</div>}
              {p.status === "pending_claim" && <div className="text-xs text-race-sub mt-2">Waiting for the winner to claim (identity verification required).</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "metrics";
  const setTab = (t) => setSearchParams(t === "metrics" ? {} : { tab: t });
  const [prefillEstate, setPrefillEstate] = useState("");
  const [fulfillBacklog, setFulfillBacklog] = useState(0);
  const useEstateInContest = (url) => { setPrefillEstate(url); setTab("create"); };
  useEffect(() => {
    api.get("/admin/prizes?status=ready_to_fulfill").then((r) => setFulfillBacklog(r.data.summary?.to_fulfill || 0)).catch(() => {});
  }, [tab]);
  return (
    <AdminShell>
      <div className="flex items-center gap-2 mb-6">
        <Shield className="text-race-red" size={28} />
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight">IBRA ADMINISTRATIVE BACK-END</h1>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <div className="w-full min-w-0">
          <TabsContent value="metrics" className="mt-0"><Metrics onGoFulfill={() => setTab("fulfillment")} /></TabsContent>
          <TabsContent value="create" className="mt-0"><CreateContest prefillEstate={prefillEstate} onConsumePrefill={() => setPrefillEstate("")} /></TabsContent>
          <TabsContent value="contests" className="mt-0"><ContestManager onGoCreate={() => setTab("create")} /></TabsContent>
          <TabsContent value="review" className="mt-0"><PrizeHolds /><ReviewQueue /></TabsContent>
          <TabsContent value="content" className="mt-0"><ContentEngine /></TabsContent>
          <TabsContent value="audit" className="mt-0"><AuditLog /></TabsContent>
          <TabsContent value="users" className="mt-0"><UsersList /></TabsContent>
          <TabsContent value="team" className="mt-0"><Team /></TabsContent>
          <TabsContent value="4thave" className="mt-0"><FourthAve onUseInContest={useEstateInContest} /></TabsContent>
          <TabsContent value="affiliates" className="mt-0"><AffiliateAdmin /></TabsContent>
          <TabsContent value="payments" className="mt-0"><PrizeFunding /></TabsContent>
          <TabsContent value="fulfillment" className="mt-0"><PrizeFulfillment /></TabsContent>
          <TabsContent value="regions" className="mt-0"><RestrictedRegions /></TabsContent>
          <TabsContent value="sms" className="mt-0"><SmsReminders /></TabsContent>
        </div>
      </Tabs>
    </AdminShell>
  );
}

function Metrics({ onGoFulfill }) {
  const [m, setM] = useState(null);
  const [prizeSummary, setPrizeSummary] = useState(null);
  const load = () => {
    api.get("/admin/metrics").then((r) => setM(r.data)).catch(() => {});
    api.get("/admin/prizes").then((r) => setPrizeSummary(r.data.summary)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const reset = async () => {
    if (!window.confirm("This permanently deletes ALL members, races, contests, BR licenses and ledger history. Admin/team accounts are kept. Continue?")) return;
    if (!window.confirm("Are you absolutely sure? This cannot be undone.")) return;
    try {
      await api.post("/admin/reset-platform");
      toast.success("Platform reset - starting from a clean slate");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!m) return null;
  const items = [
    ["Members", m.users], ["BR Licenses", m.licenses], ["Contests", m.contests],
    ["Pending", m.pending_contests], ["Races Run", m.races], ["Flagged", m.flagged],
    ["Guest→License %", `${m.guest_to_license_conv}%`],
  ];
  return (
    <div className="mt-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {items.map(([l, v]) => (
          <div key={l} className="bg-race-surface border border-race-line p-4" data-testid={`metric-${l}`}>
            <div className="tabular text-3xl text-race-red">{v}</div>
            <div className="overline mt-1">{l}</div>
          </div>
        ))}
      </div>

      {prizeSummary && (
        <div className="mt-6 bg-race-surface border border-race-line p-5" data-testid="prizes-awarded-tile">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-2xl tracking-tight flex items-center gap-2"><Gift size={18} className="text-race-red" /> Prizes Awarded</h3>
            <button onClick={() => onGoFulfill?.()} className="text-xs font-mono uppercase tracking-wide text-race-volt hover:underline" data-testid="prizes-tile-fulfill-link">Fulfillment →</button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              ["Awarded", prizeSummary.total_awarded, "metric-prizes-awarded"],
              ["Total FMV", `$${(prizeSummary.total_fmv_usd || 0).toLocaleString()}`, "metric-prizes-fmv"],
              ["Backlog", prizeSummary.backlog, "metric-prizes-backlog", prizeSummary.backlog > 0],
              ["Awaiting Claim", prizeSummary.awaiting_claim, "metric-prizes-awaiting"],
              ["Fulfilled", prizeSummary.fulfilled, "metric-prizes-fulfilled"],
            ].map(([l, v, tid, warn]) => (
              <div key={l} className="bg-race-bg border border-race-line rounded-sm p-3" data-testid={tid}>
                <div className={`tabular text-2xl ${warn ? "text-amber-400" : "text-race-txt"}`}>{v}</div>
                <div className="overline mt-1 text-[9px]">{l}</div>
              </div>
            ))}
          </div>
          {prizeSummary.to_fulfill > 0 && (
            <p className="text-amber-400 text-xs mt-3" data-testid="prizes-tile-alert">
              {prizeSummary.to_fulfill} claimed prize{prizeSummary.to_fulfill > 1 ? "s" : ""} ready to ship / issue a code.
            </p>
          )}
        </div>
      )}

      <div className="mt-8 border border-race-red/50 bg-race-surface p-6" data-testid="danger-zone">
        <h3 className="font-heading text-2xl tracking-tight text-race-red">DANGER ZONE</h3>
        <p className="text-race-sub text-sm mt-1 mb-4">Permanently delete all contests, races, BR licenses, members and ledger history to launch from a clean slate. Your admin and team accounts are kept.</p>
        <button onClick={reset} data-testid="reset-platform-btn"
          className="border border-race-red text-race-red hover:bg-race-red hover:text-race-bg font-heading text-xl tracking-wide px-6 py-2.5 rounded-sm transition-colors">
          RESET ALL PLATFORM DATA
        </button>
      </div>
    </div>
  );
}

const F = ({ label, required, optional, children }) => (
  <div>
    <label className="overline mb-1 flex items-center gap-1.5">
      <span>{label}</span>
      {required && <span className="text-race-red not-italic" title="Required" aria-label="required">*</span>}
      {optional && <span className="text-race-muted text-[9px] tracking-normal normal-case">Optional</span>}
    </label>
    {children}
  </div>
);
const inp = "w-full bg-race-bg border border-race-line focus:border-race-red outline-none px-3 py-2.5 rounded-sm text-race-txt";

// Detected local timezone, resolved from the browser (never hard-coded).
const _tzParts = (() => {
  try {
    const d = new Date();
    const long = new Intl.DateTimeFormat("en-US", { timeZoneName: "long" }).formatToParts(d).find((p) => p.type === "timeZoneName")?.value;
    const short = new Intl.DateTimeFormat("en-US", { timeZoneName: "short" }).formatToParts(d).find((p) => p.type === "timeZoneName")?.value;
    return { long, short };
  } catch { return { long: null, short: null }; }
})();
const TZ_LABEL = _tzParts.long && _tzParts.short ? `${_tzParts.long} (${_tzParts.short})` : (_tzParts.short || "your local time");

function Uploader({ label, value, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const doUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please drop an image file"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const pick = (e) => doUpload(e.target.files?.[0]);
  return (
    <div>
      <label className="overline block mb-1">{label}</label>
      <div className="flex items-center gap-3"
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); doUpload(e.dataTransfer.files?.[0]); }}>
        {value ? (
          <img src={`${BACKEND}${value}`} alt={label} className="h-14 w-14 object-cover border border-race-line rounded-sm" data-testid={`${testid}-preview`} />
        ) : (
          <div className={`h-14 w-14 border border-dashed rounded-sm flex items-center justify-center text-[10px] transition-colors ${drag ? "border-race-volt text-race-volt" : "border-race-line text-race-muted"}`} data-testid={`${testid}-dropzone`}>{drag ? "drop" : "none"}</div>
        )}
        <label className="cursor-pointer flex items-center gap-1 text-xs border border-race-line hover:border-race-txt text-race-txt px-3 py-2 rounded-sm transition-colors" data-testid={`${testid}-btn`}>
          <Upload size={12} /> {busy ? "…" : value ? "Replace" : "Upload or drop"}
          <input type="file" accept="image/*" className="hidden" onChange={pick} />
        </label>
        {value && <button onClick={() => onChange(null)} className="text-race-muted hover:text-race-red" data-testid={`${testid}-clear`}><X size={14} /></button>}
      </div>
    </div>
  );
}

// Banner formats: key -> { label, w, h }. X1/X2/X3 match the sponsor creative specs.
const AD_DIMS = {
  single: { label: "Banner X1", w: 324, h: 77 },
  double: { label: "Banner X2", w: 324, h: 132 },
  triple: { label: "Banner X3", w: 324, h: 209 },
};
// AI generator can also target the shareable event flyer (portrait poster).
const AI_DIMS = { ...AD_DIMS, flyer: { label: "Event Flyer", w: 1024, h: 1280 } };

// One-tap AI flyer themes - style directives appended to the prompt.
const FLYER_PRESETS = [
  ["Neon", "vibrant neon cyberpunk style, glowing magenta and cyan lights on a dark background, futuristic"],
  ["Retro", "retro 1980s synthwave arcade poster style, bold sunset gradient, chrome lettering, grid horizon"],
  ["Minimalist", "clean minimalist design, lots of negative space, simple bold sans-serif typography, limited two-color palette"],
];

// Text/image prompt -> AI-generated banner, auto-fit to the selected format's aspect ratio.
function AiAdGenerator({ format, onChange, testid }) {
  const [prompt, setPrompt] = useState("");
  const [ref, setRef] = useState(null);
  const [busy, setBusy] = useState(false);
  const [refBusy, setRefBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const attach = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setRefBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRef(data.url);
      toast.success("Reference image attached");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setRefBusy(false); }
  };

  const generate = async (styleText = "") => {
    const brief = [prompt.trim(), styleText].filter(Boolean).join(", ");
    if (!brief && !ref) return toast.error("Add a prompt, pick a style, or attach a reference image");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/ad/generate", { prompt: brief, format, reference_image_url: ref });
      onChange(data.url);
      toast.success(isFlyer ? "AI flyer generated & fit to size" : "AI banner generated & fit to size");
    } catch (err) { toast.error(apiError(err.response?.data?.detail), { duration: 6000 }); }
    finally { setBusy(false); }
  };

  const d = AI_DIMS[format] || AI_DIMS.single;
  const isFlyer = format === "flyer";
  const noun = isFlyer ? "flyer" : "banner";

  return (
    <div className="mt-3 border-t border-race-line/60 pt-3" data-testid={`${testid}-ai`}>
      {!open ? (
        <button type="button" onClick={() => setOpen(true)} data-testid={`${testid}-ai-open`}
          className="flex items-center gap-1.5 text-xs font-mono text-race-volt hover:text-race-txt transition-colors">
          <Sparkles size={13} /> Generate with AI
        </button>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 text-xs font-mono text-race-volt"><Sparkles size={13} /> AI {noun} generator - fits {d.label} ({d.w}×{d.h})</div>
          <textarea className={inp + " min-h-[64px]"} value={prompt} onChange={(e) => setPrompt(e.target.value)} data-testid={`${testid}-ai-prompt`}
            placeholder={isFlyer
              ? "Describe your event, e.g. 'Friday Night Trivia typing race, checkered flag, prize night'"
              : "Describe the ad, e.g. 'Redline Energy drink can bursting with lightning, dark carbon background, bold red logo space on the right'"} />
          {isFlyer && (
            <div className="flex flex-wrap items-center gap-2" data-testid={`${testid}-ai-presets`}>
              <span className="text-[11px] text-race-muted uppercase tracking-wide">One-tap themes:</span>
              {FLYER_PRESETS.map(([name, style]) => (
                <button key={name} type="button" disabled={busy} onClick={() => generate(style)} data-testid={`${testid}-ai-preset-${name.toLowerCase()}`}
                  className="flex items-center gap-1 text-xs border border-race-volt/50 hover:border-race-volt text-race-volt hover:bg-race-volt/10 disabled:opacity-50 px-3 py-1.5 rounded-full transition-colors">
                  <Sparkles size={11} /> {name}
                </button>
              ))}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <label className="cursor-pointer flex items-center gap-1 text-xs border border-race-line hover:border-race-txt text-race-txt px-3 py-1.5 rounded-sm transition-colors" data-testid={`${testid}-ai-ref-btn`}>
              <Upload size={12} /> {refBusy ? "…" : ref ? "Reference ✓" : "Reference image (optional)"}
              <input type="file" accept="image/*" className="hidden" onChange={attach} />
            </label>
            {ref && <button type="button" onClick={() => setRef(null)} className="text-race-muted hover:text-race-red" data-testid={`${testid}-ai-ref-clear`}><X size={13} /></button>}
            <button type="button" onClick={() => generate()} disabled={busy} data-testid={`${testid}-ai-generate`}
              className="ml-auto flex items-center gap-1.5 bg-race-volt/90 hover:bg-race-volt disabled:opacity-50 text-race-bg font-mono text-xs uppercase tracking-wide px-4 py-1.5 rounded-sm transition-colors">
              <Sparkles size={12} /> {busy ? "Generating…" : `Generate ${noun}`}
            </button>
          </div>
          <button type="button" onClick={() => setOpen(false)} className="text-[11px] text-race-muted hover:text-race-sub">Hide AI generator</button>
        </div>
      )}
    </div>
  );
}


// Mini preview of the exact race keyboard. When `editable`, the middle keys become inputs so the
// official can change the buttons directly (used for single contests and series alike).
function KeyboardPreview({ transmission, trackKey, editable = false, values = [], onChangeKey, validRe }) {
  const n = Math.max(0, (parseInt(transmission, 10) || 4) - 2);
  const pools = {
    alphabetical: "abcdefghijklmnopqrstuvwxyz",
    numerical: "0123456789",
    alphanumeric: "a1b2c3d4e5f6g7h8i9j0",
    special: "!@#$%&*?-_/",
  };
  const examplePool = (pools[trackKey] || pools.alphabetical).split("");
  const keyBox = "min-w-[2.75rem] h-11 px-2 flex items-center justify-center rounded-md font-mono text-sm shadow-[0_2px_0_rgba(0,0,0,0.5)]";
  return (
    <div data-testid="cc-keyboard-preview">
      <div className="overline mb-2">Live keyboard preview — {n + 2} buttons{editable ? " · tap a key below and type" : " · exactly what racers will see"}</div>
      <div className="rounded-lg border border-race-line bg-gradient-to-b from-neutral-900 to-black p-4">
        <div className="flex flex-wrap gap-2 justify-center">
          <span className={`${keyBox} bg-race-volt/20 text-race-volt border border-race-volt/40`}>www.</span>
          {Array.from({ length: n }, (_, i) => {
            if (editable) {
              const v = values[i] ?? "";
              const invalid = v.trim() && validRe && !validRe.test(v.trim());
              return (
                <input key={i} value={v} maxLength={3} placeholder="?" onChange={(e) => onChangeKey(i, e.target.value)}
                  className={`w-11 h-11 text-center font-mono text-sm bg-neutral-800 border outline-none rounded-md text-race-txt shadow-[0_2px_0_rgba(0,0,0,0.5)] ${invalid ? "border-race-red" : "border-neutral-700 focus:border-race-volt"}`}
                  data-testid={`kb-key-${i}`} />
              );
            }
            return <span key={i} data-testid={`kb-key-${i}`} className={`${keyBox} bg-neutral-800 border border-neutral-700 text-race-txt`}>{examplePool[i % examplePool.length]}</span>;
          })}
          <span className={`${keyBox} bg-race-volt/20 text-race-volt border border-race-volt/40`}>.com</span>
        </div>
      </div>
      {!editable && (
        <p className="text-race-muted text-[11px] mt-2">These are examples — actual buttons are drawn at random from the {trackKey} set at race time. Enable "Characters Used" to fix and edit them.</p>
      )}
      {editable && (
        <p className="text-race-muted text-[11px] mt-2">Tap each <span className="font-mono">?</span> key above and type one {trackKey} character. <span className="font-mono">www.</span> and <span className="font-mono">.com</span> are added automatically.</p>
      )}
    </div>
  );
}

// Easy/Medium/Hard one-tap tuning for the core race feel.
const DIFFICULTY_PRESETS = {
  easy:   { length_chars: 12, transmission: 6,  penalty_seconds: 1 },
  medium: { length_chars: 20, transmission: 8,  penalty_seconds: 2 },
  hard:   { length_chars: 28, transmission: 12, penalty_seconds: 5 },
};

const CC_DRAFT_KEY = "tr_cc_draft_v1";

// Clone an existing contest into a Create-Contest draft snapshot (fresh start time
// and Domain Estate so it publishes as a brand-new contest).
function buildDraftFromContest(c) {
  const form = {
    name: `${c.name || "Contest"} (Copy)`,
    race_type: c.race_type ?? "sprint",
    length_chars: c.length_chars ?? 20,
    transmission: c.transmission ?? 8,
    track_type: c.track_type ?? "alphabetical",
    penalty_seconds: c.penalty_seconds ?? 2,
    podium_size: c.podium_size ?? 1,
    entry_fee_credits: c.entry_fee_credits ?? 0,
    prize_pool_credits: c.prize_pool_credits ?? 0,
    invitation: c.invitation ?? "all",
    start_time: "",
    br_protocol_url: "",
    sponsor_name: c.sponsor_name ?? "",
    difficulty: c.difficulty ?? "",
    sponsor_track_url: c.sponsor_track_url ?? "",
    pre_race_ad_format: c.pre_race_ad_format ?? "single",
    post_race_ad_format: c.post_race_ad_format ?? "single",
    reminder_lead_default: c.reminder_lead_default ?? 3,
    prize_type: c.prize_type ?? "cash",
    prize_fmv_usd: c.prize_fmv_usd ?? "",
    prize_btc_amount: c.prize_btc_amount ?? "",
    web3_partner: c.web3_partner ?? "",
    prize_rate_lock: c.prize_rate_lock ?? true,
    rate_relock: c.rate_relock ?? false,
    rate_relock_lead_hours: c.rate_relock_lead_hours ?? 0,
    prize_description: c.prize_description ?? "",
    prize_image_url: c.prize_image_url ?? "",
  };
  const imgs = {
    flyer_image_url: c.flyer_image_url ?? null,
    pre_race_ad_url: c.pre_race_ad_url ?? null,
    in_race_ad_url: c.in_race_ad_url ?? null,
    post_race_ad_url: c.post_race_ad_url ?? null,
  };
  const useCustom = Array.isArray(c.custom_characters) && c.custom_characters.length > 0;
  return {
    form, imgs, mode: "single", step: 1,
    useCustom, chars: useCustom ? c.custom_characters : [""],
    hasSponsor: !!(c.sponsor_name || c.sponsor_track_url || c.pre_race_ad_url || c.in_race_ad_url || c.post_race_ad_url),
  };
}

const CC_PRESETS = [
  { key: "friday-sprint", label: "Friday Sprint", desc: "Fast alphabetical sprint · winner-take-all", Icon: Zap,
    data: { race_type: "sprint", track_type: "alphabetical", length_chars: 20, transmission: 8, penalty_seconds: 2, podium_size: 1, difficulty: "medium", prize_type: "cash", prize_pool_credits: 500, entry_fee_credits: 10 } },
  { key: "trivia-night", label: "Trivia Night", desc: "Trivia race · top-3 podium", Icon: Brain,
    data: { race_type: "trivia", track_type: "alphanumeric", length_chars: 24, transmission: 10, penalty_seconds: 3, podium_size: 3, difficulty: "hard", prize_type: "cash", prize_pool_credits: 1000, entry_fee_credits: 25 } },
  { key: "cash-100", label: "$100 Cash Race", desc: "Cash prize sprint · single winner", Icon: DollarSign,
    data: { race_type: "sprint", track_type: "alphanumeric", length_chars: 22, transmission: 9, penalty_seconds: 2, podium_size: 1, difficulty: "medium", prize_type: "cash", prize_pool_credits: 1000, entry_fee_credits: 20 } },
  { key: "warmup", label: "Beginner Warm-Up", desc: "Short & forgiving · free entry", Icon: Sparkles,
    data: { race_type: "sprint", track_type: "alphabetical", length_chars: 12, transmission: 6, penalty_seconds: 1, podium_size: 1, difficulty: "easy", prize_type: "cash", prize_pool_credits: 100, entry_fee_credits: 0 } },
];
const TEMPLATE_FIELDS = ["race_type", "track_type", "length_chars", "transmission", "penalty_seconds", "podium_size", "difficulty", "prize_type", "prize_pool_credits", "prize_fmv_usd", "entry_fee_credits", "reminder_lead_default"];

function CreateContest({ prefillEstate, onConsumePrefill }) {
  const [form, setForm] = useState({
    name: "", race_type: "sprint", length_chars: 20, transmission: 8,
    track_type: "alphabetical", penalty_seconds: 2, podium_size: 1,
    entry_fee_credits: 0, prize_pool_credits: 0, invitation: "all",
    start_time: "", br_protocol_url: "", sponsor_name: "", difficulty: "",
    sponsor_track_url: "", pre_race_ad_format: "single", post_race_ad_format: "single",
    reminder_lead_default: 3,
    prize_type: "cash", prize_fmv_usd: "", prize_btc_amount: "", web3_partner: "", prize_rate_lock: true, rate_relock: false, rate_relock_lead_hours: 0, prize_description: "", prize_image_url: "",
  });
  const [useCustom, setUseCustom] = useState(false);
  const [chars, setChars] = useState([""]);
  const [mode, setMode] = useState("single");            // single | series
  const [scheduleMode, setScheduleMode] = useState("weekday"); // weekday | days
  const [weekday, setWeekday] = useState(6);             // 0=Sun … 6=Sat
  const [seriesTime, setSeriesTime] = useState("18:00");
  const [count, setCount] = useState(4);
  const [adsMode, setAdsMode] = useState("reuse");       // reuse | ai
  const [imgs, setImgs] = useState({ flyer_image_url: null, pre_race_ad_url: null, in_race_ad_url: null, post_race_ad_url: null });
  const [busy, setBusy] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [published, setPublished] = useState(null);
  const [step, setStep] = useState(1);
  const [hasSponsor, setHasSponsor] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setImg = (k) => (v) => setImgs((s) => ({ ...s, [k]: v }));

  // ---- Browser-local draft (persists the in-progress form in THIS browser only;
  // it does not create a server contest until you publish). ----
  const DRAFT_KEY = CC_DRAFT_KEY;
  const hydrated = useRef(false);
  const [dirty, setDirty] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [estatePreview, setEstatePreview] = useState(null);
  const [btcPrice, setBtcPrice] = useState(null);
  const [partners, setPartners] = useState(["Coinbase", "Circle", "Fireblocks", "Persona"]);
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [collisionError, setCollisionError] = useState("");
  const draftSnapshot = () => ({
    form, imgs, mode, scheduleMode, weekday, seriesTime, count, adsMode, useCustom, chars, hasSponsor, step,
  });
  const writeDraft = () => { try { localStorage.setItem(DRAFT_KEY, JSON.stringify(draftSnapshot())); } catch {} };
  const clearDraft = () => { try { localStorage.removeItem(DRAFT_KEY); } catch {} };
  const saveDraft = () => { writeDraft(); setDirty(false); setLastSavedAt(Date.now()); toast.success("Draft saved in this browser"); };
  const discardDraft = () => {
    if (!window.confirm("Discard this browser draft and start a fresh contest?")) return;
    clearDraft(); setLastSavedAt(null); setDirty(false);
    setForm({ name: "", race_type: "sprint", length_chars: 20, transmission: 8, track_type: "alphabetical", penalty_seconds: 2, podium_size: 1, entry_fee_credits: 0, prize_pool_credits: 0, invitation: "all", start_time: "", br_protocol_url: "", sponsor_name: "", difficulty: "", sponsor_track_url: "", pre_race_ad_format: "single", post_race_ad_format: "single", reminder_lead_default: 3, prize_type: "cash", prize_fmv_usd: "", prize_btc_amount: "", web3_partner: "", prize_rate_lock: true, rate_relock: false, rate_relock_lead_hours: 0, prize_description: "", prize_image_url: "" });
    setImgs({ flyer_image_url: null, pre_race_ad_url: null, in_race_ad_url: null, post_race_ad_url: null });
    setMode("single"); setUseCustom(false); setChars([""]); setHasSponsor(false); setStep(1);
    toast.success("Draft discarded");
  };
  // Full wizard reset — used by "Create another" after a publish so the admin
  // always lands back on Step 1 with a clean form (previously step stayed on 4).
  const resetWizard = () => {
    setPublished(null);
    setForm({ name: "", race_type: "sprint", length_chars: 20, transmission: 8, track_type: "alphabetical", penalty_seconds: 2, podium_size: 1, entry_fee_credits: 0, prize_pool_credits: 0, invitation: "all", start_time: "", br_protocol_url: "", sponsor_name: "", difficulty: "", sponsor_track_url: "", pre_race_ad_format: "single", post_race_ad_format: "single", reminder_lead_default: 3, prize_type: "cash", prize_fmv_usd: "", prize_btc_amount: "", web3_partner: "", prize_rate_lock: true, rate_relock: false, rate_relock_lead_hours: 0, prize_description: "", prize_image_url: "" });
    setImgs({ flyer_image_url: null, pre_race_ad_url: null, in_race_ad_url: null, post_race_ad_url: null });
    setMode("single"); setUseCustom(false); setChars([""]); setHasSponsor(false); setStep(1);
    setCollisionError("");
    clearDraft();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };


  // Restore a saved draft on mount (once).
  useEffect(() => {
    const defaultStart = () => dayjs().add(1, "hour").second(0).millisecond(0).toISOString();
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        if (d.form) setForm((f) => ({ ...f, ...d.form }));
        if (d.imgs) setImgs((s) => ({ ...s, ...d.imgs }));
        if (d.mode) setMode(d.mode);
        if (d.scheduleMode) setScheduleMode(d.scheduleMode);
        if (typeof d.weekday === "number") setWeekday(d.weekday);
        if (d.seriesTime) setSeriesTime(d.seriesTime);
        if (d.count) setCount(d.count);
        if (d.adsMode) setAdsMode(d.adsMode);
        if (typeof d.useCustom === "boolean") setUseCustom(d.useCustom);
        if (Array.isArray(d.chars)) setChars(d.chars);
        if (typeof d.hasSponsor === "boolean") setHasSponsor(d.hasSponsor);
        setLastSavedAt(Date.now());
        toast.message("Draft restored", { description: "Picked up your unpublished contest from this browser." });
      }
    } catch {}
    // Never show a past/empty start date - default to today (never a previous date).
    setForm((f) => (!f.start_time || dayjs(f.start_time).isBefore(dayjs())) ? { ...f, start_time: defaultStart() } : f);
    hydrated.current = true;
    // eslint-disable-next-line
  }, []);

  // Autosave to the browser (debounced) whenever the form changes - only once
  // there is meaningful content worth keeping.
  useEffect(() => {
    if (!hydrated.current) return;
    const meaningful = !!(form.name.trim() || form.start_time || hasSponsor || useCustom || imgs.flyer_image_url);
    if (!meaningful) { setDirty(false); return; }
    setDirty(true);
    const t = setTimeout(() => { writeDraft(); setDirty(false); setLastSavedAt(Date.now()); }, 900);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [form, imgs, mode, scheduleMode, weekday, seriesTime, count, adsMode, useCustom, chars, hasSponsor]);


  // Transmission = total buttons (4–16). www. and .com are always 2 of them, so the
  // customizable character count is transmission − 2.
  const transmission = Math.max(4, Math.min(16, parseInt(form.transmission, 10) || 4));
  const customSlots = transmission - 2;
  const trackCfg = TRACK_TYPES[form.track_type] || TRACK_TYPES.alphabetical;
  const applyDifficulty = (key) => setForm((f) => ({ ...f, ...DIFFICULTY_PRESETS[key], difficulty: key }));
  const activeDifficulty = Object.keys(DIFFICULTY_PRESETS).find((k) => {
    const p = DIFFICULTY_PRESETS[k];
    return +form.length_chars === p.length_chars && +form.transmission === p.transmission && +form.penalty_seconds === p.penalty_seconds;
  }) || "";

  // Keep the number of custom-character inputs locked to (transmission − 2).
  useEffect(() => {
    if (!useCustom) return;
    setChars((a) => {
      const next = a.slice(0, customSlots);
      while (next.length < customSlots) next.push("");
      return next;
    });
  }, [customSlots, useCustom]);

  // Changing the track type re-sanitizes any already-typed buttons so stale
  // characters (e.g. letters left over after switching to numerical) are dropped.
  useEffect(() => {
    setChars((a) => a.map((x) => sanitizeForTrack(x, form.track_type)));
  }, [form.track_type]);

  // A Domain Estate selected in the 4th AVE manager drops straight into the field.
  useEffect(() => {
    if (prefillEstate) {
      set("br_protocol_url", prefillEstate);
      toast.success("Domain Estate loaded into the contest form");
      onConsumePrefill?.();
    }
    // eslint-disable-next-line
  }, [prefillEstate]);

  // Estate preview: peek (non-consuming) at the estate that will auto-draw on publish.
  useEffect(() => {
    api.get("/crypto/btc-price").then((r) => setBtcPrice(r.data.usd)).catch(() => {});
    api.get("/web3/partners").then((r) => r.data?.partners?.length && setPartners(r.data.partners)).catch(() => {});
    api.get("/admin/contest-templates").then((r) => setTemplates(r.data || [])).catch(() => {});
  }, []);

  const applyTemplate = (data, label) => {
    setForm((f) => ({ ...f, ...data }));
    toast.success(`Loaded "${label}"`, { description: "Tweak anything you like, then continue." });
  };
  const saveAsTemplate = async () => {
    const nm = templateName.trim();
    if (!nm) return toast.error("Give the template a name");
    setSavingTemplate(true);
    try {
      const data = {};
      TEMPLATE_FIELDS.forEach((k) => { data[k] = form[k]; });
      const { data: t } = await api.post("/admin/contest-templates", { name: nm, data });
      setTemplates((ts) => [t, ...ts]);
      setTemplateName("");
      toast.success(`Saved "${nm}" as a reusable template`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not save template"); }
    finally { setSavingTemplate(false); }
  };
  const deleteTemplate = async (id) => {
    try {
      await api.delete(`/admin/contest-templates/${id}`);
      setTemplates((ts) => ts.filter((t) => t.id !== id));
    } catch { toast.error("Could not delete template"); }
  };

  // ---- D. Start-time quick presets & guard ----
  const setStart = (d) => { set("start_time", d.toISOString()); setCollisionError(""); };
  const nextWeekday = (target, h, m) => {
    let d = dayjs().hour(h).minute(m).second(0).millisecond(0);
    while (d.day() !== target || !d.isAfter(dayjs())) d = d.add(1, "day");
    return d;
  };
  const todayOrNext = (h, m) => {
    let d = dayjs().hour(h).minute(m).second(0).millisecond(0);
    if (!d.isAfter(dayjs())) d = d.add(1, "day");
    return d;
  };
  const QUICK_TIMES = [
    { label: "In 1 hour", make: () => dayjs().add(1, "hour").second(0).millisecond(0) },
    { label: "Tonight 8:00 PM", make: () => todayOrNext(20, 0) },
    { label: "Tomorrow 6:00 PM", make: () => dayjs().add(1, "day").hour(18).minute(0).second(0).millisecond(0) },
    { label: "This Friday 7:00 PM", make: () => nextWeekday(5, 19, 0) },
  ];
  const startDt = form.start_time ? dayjs(form.start_time) : null;
  const startWarn = startDt ? (startDt.isBefore(dayjs()) ? "past" : startDt.isBefore(dayjs().add(15, "minute")) ? "soon" : "") : "";
  useEffect(() => {
    if (step === 4 && mode === "single" && !form.br_protocol_url) {
      api.get("/4thave/preview").then((r) => setEstatePreview(r.data)).catch(() => setEstatePreview(null));
    }
    // eslint-disable-next-line
  }, [step, mode, form.br_protocol_url]);

  const generateEstate = async () => {
    setDrawing(true);
    try {
      const { data } = await api.post("/4thave/draw");
      set("br_protocol_url", data.url);
      toast.success("Domain Estate generated from the IBRA repository");
    } catch (e) {
      const msg = apiError(e.response?.data?.detail);
      toast.error(msg, { duration: 6000 });
    } finally { setDrawing(false); }
  };

  const cleanChars = chars.map((c) => c.trim()).filter(Boolean);

  const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const seriesCount = Math.max(2, Math.min(12, parseInt(count, 10) || 2));
  // Compute the ISO start times for the series from the schedule settings (local timezone).
  const buildSeriesTimes = () => {
    const [h, m] = (seriesTime || "18:00").split(":").map((x) => parseInt(x, 10) || 0);
    const out = [];
    if (scheduleMode === "weekday") {
      let d = dayjs().hour(h).minute(m).second(0).millisecond(0);
      let guard = 0;
      while (out.length < seriesCount && guard < 400) {
        if (d.day() === Number(weekday) && d.isAfter(dayjs())) { out.push(d); d = d.add(7, "day"); }
        else d = d.add(1, "day");
        guard++;
      }
    } else {
      let d = dayjs().add(1, "day").hour(h).minute(m).second(0).millisecond(0);
      for (let i = 0; i < seriesCount; i++) { out.push(d); d = d.add(1, "day"); }
    }
    return out;
  };
  const seriesDates = mode === "series" ? buildSeriesTimes() : [];

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Contest name is required");
    if (mode === "single" && !form.start_time) return toast.error("Start time required");
    if (mode === "single" && startWarn === "past") { setStep(1); return toast.error("Start time is in the past — pick a future time"); }
    if (form.prize_type === "bitcoin" && !form.web3_partner) return toast.error("Select a Web3 / KYC partner for the Bitcoin prize");
    if (mode === "series" && seriesDates.length !== seriesCount) return toast.error("Pick a valid schedule for the series");
    if (useCustom) {
      const filled = chars.map((c) => c.trim());
      if (filled.length !== customSlots || filled.some((c) => !c))
        return toast.error(`Fill all ${customSlots} custom buttons - www. and .com are added automatically`);
      for (const tok of filled)
        if (!trackCfg.re.test(tok))
          return toast.error(`"${tok}" isn't valid for a ${trackCfg.label} track`);
      if (form.track_type === "alphanumeric" && !alnumHasBoth(filled))
        return toast.error("An alphanumeric track needs at least one letter AND one number across the buttons");
      if (trackCfg.requireSpecial && ![...filled.join("")].some((ch) => SPECIALS.includes(ch)))
        return toast.error("A special-characters track must include at least one special character (e.g. ! @ # $ %)");
    }
    setBusy(true);
    try {
      const shared = {
        ...form, ...imgs,
        length_chars: +form.length_chars, transmission,
        penalty_seconds: +form.penalty_seconds, podium_size: +form.podium_size,
        entry_fee_credits: +form.entry_fee_credits, prize_pool_credits: +form.prize_pool_credits,
        prize_type: form.prize_type || "cash",
        prize_fmv_usd: form.prize_type !== "cash" ? (+form.prize_fmv_usd || 0) : null,
        prize_btc_amount: form.prize_type === "bitcoin" ? (+form.prize_btc_amount || 0) : null,
        web3_partner: form.prize_type === "bitcoin" ? (form.web3_partner || null) : null,
        prize_rate_lock: form.prize_type === "bitcoin" ? !!form.prize_rate_lock : null,
        rate_relock: form.prize_type === "bitcoin" && form.prize_rate_lock ? !!form.rate_relock : false,
        rate_relock_lead_hours: Number(form.rate_relock_lead_hours || 0),
        prize_description: (form.prize_type === "gift_card" || form.prize_type === "physical") ? (form.prize_description || "") : "",
        prize_image_url: (form.prize_type === "gift_card" || form.prize_type === "physical") ? (form.prize_image_url || "") : "",
        sponsor_name: form.sponsor_name || null, difficulty: form.difficulty || null,
        sponsor_track_url: form.sponsor_track_url || null,
        reminder_lead_default: +form.reminder_lead_default || 3,
        custom_characters: useCustom ? cleanChars : null,
      };
      if (mode === "series") {
        const payload = {
          ...shared,
          start_time: seriesDates[0].toISOString(),   // satisfies base model; per-contest below
          start_times: seriesDates.map((d) => d.toISOString()),
          ads_mode: adsMode,
        };
        const { data } = await api.post("/contests/series", payload);
        setPublished({ series: true, count: data.count, name: form.name, ads_mode: data.ads_mode,
                       ids: data.contest_ids || [], dates: seriesDates.map((d) => d.toISOString()) });
        clearDraft();
        toast.success(`Series published - ${data.count} contests added to the calendar`);
      } else {
        const payload = { ...shared, start_time: new Date(form.start_time).toISOString() };
        const { data } = await api.post("/contests", payload);
        setPublished(data);
        clearDraft();
        toast.success("Contest sanctioned & published");
      }
    } catch (e) {
      const status = e.response?.status;
      const detail = apiError(e.response?.data?.detail);
      if (status === 409) {
        setCollisionError(detail || "That start time is already taken. Please pick a different time.");
        toast.error(detail || "That start time is already taken.", { duration: 6000 });
        go(1);
      } else {
        toast.error(detail);
      }
    } finally { setBusy(false); }
  };

  if (published) {
    const link = `${window.location.origin}/contests/${published.id}`;
    if (published.series) {
      return (
        <div className="mt-4 bg-race-surface border border-race-line p-6 text-center max-w-3xl mx-auto" data-testid="series-published">
          <h3 className="font-heading text-3xl tracking-tight mb-1 text-race-volt">SERIES PUBLISHED</h3>
          <p className="text-race-txt font-mono text-sm mb-2">{published.name}</p>
          <p className="text-race-txt text-lg mb-1">{published.count} contests added to the calendar</p>
          <p className="text-race-sub text-sm mb-5">
            Each has its own Domain Estate & track.{" "}
            {published.ads_mode === "ai" ? "AI is generating aligned ad variants in the background." : "Ads reused across all contests."}
          </p>
          {Array.isArray(published.ids) && published.ids.length > 0 && (
            <>
              <p className="text-race-sub text-xs uppercase tracking-widest mb-3">Scan or share each contest</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6" data-testid="series-qr-grid">
                {published.ids.map((cid, i) => {
                  const qlink = `${window.location.origin}/contests/${cid}`;
                  const when = published.dates?.[i];
                  return (
                    <div key={cid} className="bg-race-bg border border-race-line rounded-md p-3 flex flex-col items-center" data-testid={`series-qr-${i}`}>
                      <div className="bg-white p-2 rounded">
                        <QRCodeSVG value={qlink} size={120} fgColor="#0B0B0C" bgColor="#FFFFFF" />
                      </div>
                      <p className="text-race-txt font-mono text-xs mt-2 text-center">{published.name} #{i + 1}</p>
                      {when && <p className="text-race-sub text-[11px] text-center">{dayjs(when).format("MMM D · h:mm A")}</p>}
                    </div>
                  );
                })}
              </div>
            </>
          )}
          <button onClick={resetWizard}
            data-testid="cc-new" className="mt-2 border border-race-line hover:border-race-txt text-race-txt px-6 py-2 rounded-sm transition-colors">Create another</button>
        </div>
      );
    }
    return (
      <div className="mt-4 bg-race-surface border border-race-line p-6 text-center max-w-md mx-auto" data-testid="contest-published">
        <h3 className="font-heading text-3xl tracking-tight mb-1 text-race-volt">CONTEST PUBLISHED</h3>
        <p className="text-race-txt font-mono text-sm mb-1">{published.name}</p>
        {published.start_time && (
          <p className="text-race-sub text-sm mb-4" data-testid="published-when">
            {new Date(published.start_time).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
            {" · "}
            {new Date(published.start_time).toLocaleTimeString([], { hour: "numeric", minute: "2-digit", timeZoneName: "short" })}
            {" · "}<span className="uppercase">{published.race_type}</span>
          </p>
        )}
        <div className="inline-block bg-white p-4 rounded-md">
          <QRCodeSVG value={link} size={190} fgColor="#0B0B0C" bgColor="#FFFFFF" data-testid="contest-qr" />
        </div>
        <p className="text-race-sub text-xs mt-3 break-all">{link}</p>
        <button onClick={resetWizard}
          data-testid="cc-new" className="mt-5 border border-race-line hover:border-race-txt text-race-txt px-6 py-2 rounded-sm transition-colors">Create another</button>
      </div>
    );
  }

  const steps = ["CONTEST SETUP", "RACE SETTINGS", "SPONSORSHIP & MEDIA", "REVIEW & PUBLISH"];
  const go = (n) => { setStep(n); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const stepValid = (s) => {
    if (s === 1) {
      if (!form.name.trim()) { toast.error("Contest name is required"); return false; }
      if (mode === "single" && !form.start_time) { toast.error("Start date & time is required"); return false; }
      if (mode === "series" && seriesDates.length !== seriesCount) { toast.error("Pick a valid series schedule"); return false; }
    }
    if (s === 2) {
      const t = parseInt(form.transmission, 10);
      if (!(t >= 4 && t <= 16)) { toast.error("Transmission must be between 4 and 16"); return false; }
    }
    return true;
  };
  const next = () => { if (stepValid(step)) go(step + 1); };
  const prizeLabel = form.prize_type === "cash" ? "Cash (Credits)" : form.prize_type === "bitcoin" ? "Bitcoin" : form.prize_type === "gift_card" ? "Gift Card" : "Physical Item";
  const prizeValue = form.prize_type === "cash" ? `${form.prize_pool_credits || 0} Credits`
    : form.prize_type === "bitcoin" ? `$${form.prize_fmv_usd || 0}${form.prize_btc_amount ? ` · ${form.prize_btc_amount} BTC` : ""}${form.web3_partner ? ` · via ${form.web3_partner}` : ""} · ${form.prize_rate_lock ? "rate locked at publish" : "rate floats to payout"}`
    : `$${form.prize_fmv_usd || 0} FMV${form.prize_description ? ` · ${form.prize_description}` : ""}`;

  // ---- C. Pre-flight checklist ----
  const preflight = (() => {
    const c = [];
    c.push(form.name.trim()
      ? { label: "Contest name set", status: "pass" }
      : { label: "Contest name is required", status: "fail" });
    if (mode === "series") {
      c.push(seriesDates.length === seriesCount
        ? { label: `Series schedule valid (${seriesDates.length} dates)`, status: "pass" }
        : { label: "Pick a valid series schedule", status: "fail" });
    } else if (!form.start_time) {
      c.push({ label: "Start date & time is required", status: "fail" });
    } else if (startWarn === "past") {
      c.push({ label: "Start time is in the past", status: "fail", detail: "Choose a future time." });
    } else if (startWarn === "soon") {
      c.push({ label: "Start time is under 15 min away", status: "warn", detail: "Racers may miss reminders." });
    } else {
      c.push({ label: `Starts ${dayjs(form.start_time).format("ddd, MMM D · h:mm A")}`, status: "pass" });
    }
    if (form.prize_type === "cash") {
      c.push(+form.prize_pool_credits > 0
        ? { label: `Cash prize: ${form.prize_pool_credits} credits`, status: "pass" }
        : { label: "Cash prize is 0 credits", status: "warn", detail: "Winners get nothing unless you set a prize." });
    } else if (form.prize_type === "bitcoin") {
      if (!form.web3_partner) c.push({ label: "Bitcoin prize needs a Web3 / KYC partner", status: "fail" });
      else if (!(+form.prize_fmv_usd > 0 || +form.prize_btc_amount > 0)) c.push({ label: "Bitcoin prize amount is 0", status: "warn" });
      else c.push({ label: `Bitcoin prize via ${form.web3_partner}`, status: "pass" });
    } else {
      const okVal = +form.prize_fmv_usd > 0;
      const okDesc = !!(form.prize_description || "").trim();
      if (okVal && okDesc) c.push({ label: `${prizeLabel}: ${form.prize_description}`, status: "pass" });
      else c.push({ label: `${prizeLabel} needs a value & description`, status: "warn", detail: !okVal ? "FMV is 0." : "No item description." });
    }
    if (hasSponsor) {
      const missing = [["Pre-Race", imgs.pre_race_ad_url], ["In-Race", imgs.in_race_ad_url], ["Finish-Line", imgs.post_race_ad_url]].filter(([, v]) => !v).map(([l]) => l);
      c.push(missing.length === 0
        ? { label: "All sponsor ad slots have creative", status: "pass" }
        : { label: `Missing ad creative: ${missing.join(", ")}`, status: "warn", detail: "Empty slots fall back to a house ad." });
    } else {
      c.push({ label: "No sponsor / ads (optional)", status: "pass" });
    }
    if (mode === "single") {
      c.push(form.br_protocol_url
        ? { label: "4th AVE Domain Estate selected", status: "pass" }
        : { label: "Domain Estate auto-draws on publish", status: "pass" });
    }
    return c;
  })();
  const hasHardFail = preflight.some((x) => x.status === "fail");

  return (
    <div className="mt-4 max-w-[1200px] pb-24" data-testid="create-contest-form">
      {/* Single contest vs Series - compact segmented control */}
      <div className="inline-flex rounded-sm overflow-hidden border border-race-line" data-testid="cc-mode">
        {[["single", "Single Contest"], ["series", "Series"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setMode(k)} data-testid={`cc-mode-${k}`}
            className={`px-5 py-1.5 text-xs font-mono uppercase tracking-wide transition-colors ${mode === k ? "bg-white text-race-bg" : "bg-race-surface text-race-sub hover:text-race-txt"}`}>{l}</button>
        ))}
      </div>

      {/* Progress indicator */}
      <div className="flex items-center gap-1.5 my-6 overflow-x-auto" data-testid="cc-stepper">
        {steps.map((label, i) => {
          const n = i + 1; const done = n < step; const cur = n === step;
          const clickable = n < step;
          return (
            <React.Fragment key={n}>
              <button type="button" onClick={() => (clickable ? go(n) : null)} disabled={n > step} data-testid={`cc-step-${n}`}
                className={`flex items-center gap-2 shrink-0 px-3 py-2 rounded-sm border text-[11px] font-mono uppercase tracking-wide transition-colors ${cur ? "border-white bg-white/10 text-white" : done ? "border-race-sub text-race-txt hover:border-white cursor-pointer" : "border-race-line/70 text-race-sub cursor-default"}`}>
                <span className={`w-5 h-5 flex items-center justify-center rounded-full text-[10px] font-bold ${cur ? "bg-white text-race-bg" : done ? "bg-race-txt text-race-bg" : "bg-race-line text-race-sub"}`}>{done ? "✓" : n}</span>
                {label}
              </button>
              {n < steps.length && <span className="text-race-line/70 shrink-0">—</span>}
            </React.Fragment>
          );
        })}
      </div>

      {/* STEP 1 — CONTEST SETUP */}
      <div style={{ display: step === 1 ? "block" : "none" }} className="space-y-6" data-testid="cc-step1">
        <div><h3 className="font-heading text-2xl tracking-tight">CONTEST SETUP</h3><p className="text-race-sub text-sm">Start with the basic information for this contest.</p></div>

      {/* A. Quick-Start Templates */}
      <div className="bg-race-surface border border-race-line p-5 space-y-4" data-testid="cc-templates">
        <div className="flex items-center gap-2">
          <LayoutTemplate size={16} className="text-race-volt" />
          <span className="overline">Quick-start from a template</span>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="cc-presets">
          {CC_PRESETS.map((p) => (
            <button key={p.key} type="button" onClick={() => applyTemplate(p.data, p.label)} data-testid={`cc-preset-${p.key}`}
              className="text-left border border-race-line hover:border-race-volt rounded-md p-3 transition-colors group">
              <div className="flex items-center gap-2 mb-1">
                <p.Icon size={15} className="text-race-volt shrink-0" />
                <span className="font-semibold text-sm text-race-txt group-hover:text-race-volt transition-colors">{p.label}</span>
              </div>
              <span className="text-[11px] text-race-sub leading-snug block">{p.desc}</span>
            </button>
          ))}
        </div>
        {templates.length > 0 && (
          <div className="border-t border-race-line/60 pt-3">
            <div className="overline text-race-sub mb-2">Your saved templates</div>
            <div className="flex flex-wrap gap-2" data-testid="cc-my-templates">
              {templates.map((t) => (
                <span key={t.id} className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full bg-race-bg border border-race-line text-xs">
                  <button type="button" onClick={() => applyTemplate(t.data, t.name)} data-testid={`cc-template-${t.id}`} className="text-race-txt hover:text-race-volt transition-colors">{t.name}</button>
                  <button type="button" onClick={() => deleteTemplate(t.id)} data-testid={`cc-template-del-${t.id}`} className="text-race-muted hover:text-race-red transition-colors"><X size={12} /></button>
                </span>
              ))}
            </div>
          </div>
        )}
        <div className="border-t border-race-line/60 pt-3 flex items-center gap-2 flex-wrap">
          <input className={inp + " flex-1 min-w-[180px]"} placeholder="Save current settings as a template…" value={templateName} onChange={(e) => setTemplateName(e.target.value)} data-testid="cc-template-name" />
          <button type="button" onClick={saveAsTemplate} disabled={savingTemplate} data-testid="cc-save-template"
            className="inline-flex items-center gap-1.5 border border-race-line hover:border-race-volt text-race-sub hover:text-race-volt px-4 py-2 rounded-sm text-xs font-mono uppercase tracking-wide disabled:opacity-50 transition-colors">
            <Save size={13} /> {savingTemplate ? "Saving…" : "Save template"}
          </button>
        </div>
      </div>
      {mode === "series" && (
        <div className="bg-race-surface border border-race-volt/40 p-6 space-y-4" data-testid="cc-series-block">
          <h3 className="font-heading text-2xl tracking-tight text-race-volt">SERIES SCHEDULE</h3>
          <p className="text-race-sub text-sm">Books multiple contests at once. Each gets its own Domain Estate and a fresh track; all share the settings below (name gets #1, #2 …).</p>
          <div className="flex rounded-sm overflow-hidden border border-race-line w-full max-w-sm">
            {[["weekday", "Weekly (weekday)"], ["days", "Next N days"]].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setScheduleMode(k)} data-testid={`cc-sched-${k}`}
                className={`flex-1 px-3 py-1.5 text-xs font-mono uppercase transition-colors ${scheduleMode === k ? "bg-race-volt text-race-bg" : "text-race-sub hover:text-race-txt"}`}>{l}</button>
            ))}
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {scheduleMode === "weekday" && (
              <F label="Day of week"><select className={inp} value={weekday} onChange={(e) => setWeekday(+e.target.value)} data-testid="cc-weekday">{WEEKDAYS.map((w, i) => <option key={i} value={i}>{w}</option>)}</select></F>
            )}
            <F label="Time"><input type="time" className={inp} value={seriesTime} onChange={(e) => setSeriesTime(e.target.value)} data-testid="cc-series-time" /></F>
            <div>
              <label className="overline block mb-1">How many contests</label>
              <div className="flex gap-2 items-center">
                {[3, 4, 5].map((n) => (
                  <button key={n} type="button" onClick={() => setCount(n)} data-testid={`cc-count-${n}`}
                    className={`px-3 py-2 rounded-sm text-sm font-mono border transition-colors ${seriesCount === n ? "bg-race-red border-race-red text-race-bg" : "border-race-line text-race-sub hover:border-race-txt"}`}>{n}</button>
                ))}
                <input type="number" min="2" max="12" className={inp + " w-20"} value={count} onChange={(e) => setCount(e.target.value)} data-testid="cc-count-custom" />
              </div>
            </div>
          </div>
          <div>
            <label className="overline block mb-1">Advertising across the series</label>
            <div className="space-y-1.5">
              {[["reuse", "Reuse the same banners & flyer for every contest"], ["ai", "AI-generate an aligned variant per contest (uses AI credits)"]].map(([k, l]) => (
                <label key={k} className="flex items-center gap-2 text-sm text-race-txt cursor-pointer" data-testid={`cc-ads-${k}`}>
                  <input type="radio" name="adsmode" checked={adsMode === k} onChange={() => setAdsMode(k)} /> {l}
                </label>
              ))}
            </div>
          </div>
          <div className="border-t border-race-line/60 pt-3">
            <div className="overline mb-2">Scheduled dates ({seriesDates.length})</div>
            <div className="flex flex-wrap gap-2" data-testid="cc-series-preview">
              {seriesDates.map((d, i) => (
                <span key={i} className="px-3 py-1.5 rounded-full bg-race-bg border border-race-line text-xs font-mono text-race-txt">
                  #{i + 1} {d.format("ddd, MMM D · h:mm A")}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="bg-race-surface border border-race-line p-6 grid md:grid-cols-2 gap-x-6 gap-y-4">
        <F label="Contest Name" required><input className={inp} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Friday Night Trivia" data-testid="cc-name" /><span className="text-race-muted text-[11px] mt-1 block">Shown on the published QR.{form.name.trim() ? " ✓ Looks good" : ""}</span></F>
        <F label="Race Type"><select className={inp} value={form.race_type} onChange={(e) => set("race_type", e.target.value)} data-testid="cc-racetype"><option value="sprint">Sprint</option><option value="trivia">Trivia</option></select></F>
        {mode === "single" && <F label="Start Date & Time" required>
          <div className="border border-race-line/70 rounded-sm bg-race-bg/40 p-2" data-testid="cc-datetime-group"><DateTimePicker value={form.start_time} onChange={(iso) => { set("start_time", iso); setCollisionError(""); }} testid="cc-start" /></div>
          <div className="flex flex-wrap gap-1.5 mt-2" data-testid="cc-quick-times">
            {QUICK_TIMES.map((q) => (
              <button key={q.label} type="button" onClick={() => setStart(q.make())} data-testid={`cc-quick-${q.label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                className="inline-flex items-center gap-1 border border-race-line hover:border-race-volt text-race-sub hover:text-race-volt px-2.5 py-1 rounded-full text-[11px] transition-colors">
                <Clock size={11} /> {q.label}
              </button>
            ))}
          </div>
          {startWarn === "past" && <p className="text-race-red text-[11px] mt-1.5 flex items-center gap-1" data-testid="cc-start-warn"><AlertTriangle size={12} /> This time is in the past — pick a future time.</p>}
          {startWarn === "soon" && <p className="text-amber-400 text-[11px] mt-1.5 flex items-center gap-1" data-testid="cc-start-warn"><AlertTriangle size={12} /> Under 15 minutes away — racers may miss reminders.</p>}
          {collisionError && <p className="text-race-red text-[11px] mt-1.5 flex items-center gap-1" data-testid="cc-collision-warn"><AlertTriangle size={12} /> {collisionError}</p>}
          <p className="text-race-muted text-[11px] mt-1" data-testid="cc-tz">Times shown in {TZ_LABEL}.</p>
        </F>}
        <F label="Reminder before start"><select className={inp} value={form.reminder_lead_default} onChange={(e) => set("reminder_lead_default", e.target.value)} data-testid="cc-reminder">{[3, 5, 10].map((m) => <option key={m} value={m}>{m} min</option>)}</select></F>
        <F label="Podium Size (1–100)"><input type="number" min="1" max="100" className={inp} value={form.podium_size} onChange={(e) => set("podium_size", e.target.value)} data-testid="cc-podium" /><span className="text-race-muted text-[11px] mt-1 block">How many finishing places win - 1 = winner-take-all.</span></F>
        <F label="Entry Fee (Credits)"><input type="number" className={inp} value={form.entry_fee_credits} onChange={(e) => set("entry_fee_credits", e.target.value)} data-testid="cc-entry" /></F>
        <F label="Prize Type"><select className={inp} value={form.prize_type} onChange={(e) => set("prize_type", e.target.value)} data-testid="cc-prize-type">
          <option value="cash">Cash (credits)</option>
          <option value="bitcoin">Bitcoin</option>
          <option value="gift_card">Gift Card</option>
          <option value="physical">Physical Item</option>
        </select></F>
        {form.prize_type === "cash" ? (
          <F label="Racer Prize (Credits)"><input type="number" className={inp} value={form.prize_pool_credits} onChange={(e) => set("prize_pool_credits", e.target.value)} data-testid="cc-prize" /><span className="text-race-muted text-[11px] mt-1 block">A fixed number of credits (not a percentage) awarded to the winner(s) - not the total entry pot.</span></F>
        ) : form.prize_type === "bitcoin" ? (
          <F label="Bitcoin Prize">
            <div className="grid grid-cols-2 gap-2">
              <div className="relative">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-race-sub text-sm pointer-events-none">$</span>
                <input type="number" min="0" step="1" className={`${inp} pl-6`} value={form.prize_fmv_usd ?? ""} data-testid="cc-prize-usd"
                  onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, prize_fmv_usd: v, prize_btc_amount: btcPrice && v !== "" ? +(+v / btcPrice).toFixed(8) : f.prize_btc_amount })); }}
                  placeholder="USD" />
              </div>
              <div className="relative">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#F7931A] text-sm pointer-events-none font-bold">₿</span>
                <input type="number" min="0" step="0.00000001" className={`${inp} pl-6`} value={form.prize_btc_amount ?? ""} data-testid="cc-prize-btc"
                  onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, prize_btc_amount: v, prize_fmv_usd: btcPrice && v !== "" ? +(+v * btcPrice).toFixed(2) : f.prize_fmv_usd })); }}
                  placeholder="BTC" />
              </div>
            </div>
            <span className="text-race-muted text-[11px] mt-1 block">{btcPrice ? `Live rate: 1 BTC = $${btcPrice.toLocaleString()}. Enter either field - the other converts in real time.` : "Fetching live BTC rate…"}</span>
          </F>
        ) : (
          <F label="Prize Value (USD FMV)"><input type="number" min="0" className={inp} value={form.prize_fmv_usd} onChange={(e) => set("prize_fmv_usd", e.target.value)} placeholder="e.g. 500" data-testid="cc-prize-fmv" /></F>
        )}
        {form.prize_type === "bitcoin" && (
          <>
            <F label="Web3 / KYC Partner"><select className={inp} value={form.web3_partner || ""} onChange={(e) => set("web3_partner", e.target.value)} data-testid="cc-web3-partner">
              <option value="">Select a partner…</option>
              {partners.map((p) => <option key={p} value={p}>{p}</option>)}
            </select><span className="text-race-muted text-[11px] mt-1 block">Verifies the winner's identity and settles the payout.</span></F>
            <div className="md:col-span-2">
              <label className="flex items-start gap-2 p-3 border border-race-line rounded-sm cursor-pointer hover:border-race-volt transition-colors" data-testid="cc-rate-lock-row">
                <input type="checkbox" checked={!!form.prize_rate_lock} onChange={(e) => set("prize_rate_lock", e.target.checked)} data-testid="cc-rate-lock" className="mt-0.5 accent-[#F7931A]" />
                <span className="text-xs">
                  <span className="text-race-txt font-semibold">Lock USD ↔ BTC rate at publish</span>
                  <span className="block text-race-sub mt-0.5">{form.prize_rate_lock
                    ? `Fixes the exact Bitcoin payout${btcPrice ? ` at today's rate (1 BTC = $${btcPrice.toLocaleString()})` : ""}. The winner receives the locked BTC amount no matter how the price moves.`
                    : "Payout stays USD-denominated; the exact BTC is set at the moment the winner claims (rate floats)."}</span>
                </span>
              </label>
              {form.prize_rate_lock && mode === "single" && (
                <div className="mt-2 pl-3 border-l-2 border-race-line" data-testid="cc-relock-wrap">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="checkbox" checked={!!form.rate_relock} onChange={(e) => set("rate_relock", e.target.checked)} data-testid="cc-rate-relock" className="mt-0.5 accent-[#F7931A]" />
                    <span className="text-xs">
                      <span className="text-race-txt">Re-lock closer to race day</span>
                      <span className="block text-race-sub mt-0.5">If this contest is scheduled well ahead, re-snapshot the rate near the start so the payout tracks a near-race-day price instead of a stale one.</span>
                    </span>
                  </label>
                  {form.rate_relock && (
                    <div className="mt-2 flex items-center gap-2 text-xs">
                      <span className="text-race-sub">Re-lock</span>
                      <select value={form.rate_relock_lead_hours} onChange={(e) => set("rate_relock_lead_hours", e.target.value)} data-testid="cc-relock-lead"
                        className="bg-race-bg border border-race-line rounded-sm px-2 py-1 text-race-txt focus:border-race-red outline-none">
                        <option value={0}>at race start</option>
                        <option value={1}>1 hour before start</option>
                        <option value={24}>24 hours before start</option>
                        <option value={72}>3 days before start</option>
                      </select>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="md:col-span-2 bg-race-bg border border-race-line rounded-sm p-4" data-testid="cc-crypto-process">
              <div className="overline text-race-sub mb-2">How the winner gets paid</div>
              <div className="flex flex-wrap items-center gap-2 text-[11px] text-race-txt">
                {["Wins race", "Secure email link", "Opens partner webview", `${form.web3_partner || "Partner"} verifies KYC`, "Bitcoin paid out"].map((s, i, arr) => (
                  <React.Fragment key={i}>
                    <span className="border border-race-line rounded-full px-2.5 py-1 bg-race-surface">{s}</span>
                    {i < arr.length - 1 && <span className="text-race-muted">→</span>}
                  </React.Fragment>
                ))}
              </div>
              <p className="text-[11px] text-race-sub mt-2">Email is the secure bridge to your Web3/KYC partner - the partner runs identity verification and the on-chain payout.</p>
            </div>
          </>
        )}
        {(form.prize_type === "gift_card" || form.prize_type === "physical") && (
          <div className="md:col-span-2">
            <label className="overline block mb-1">Prize Description</label>
            <input className={inp} value={form.prize_description} onChange={(e) => set("prize_description", e.target.value)}
              placeholder={form.prize_type === "gift_card" ? "e.g. $100 Amazon gift card" : "e.g. PlayStation 5"} data-testid="cc-prize-desc" />
            <div className="mt-3">
              <Uploader label="Prize photo (shown to the winner)" value={form.prize_image_url} onChange={(url) => set("prize_image_url", url || "")} testid="cc-prize-image" />
            </div>
            <p className="text-[11px] text-race-sub mt-2">Winners claim non-cash prizes after a one-time identity check. Fulfilled manually from Admin → Prize Fulfillment.</p>
          </div>
        )}
      </div>
      </div>

      {/* STEP 2 — RACE SETTINGS */}
      <div style={{ display: step === 2 ? "block" : "none" }} className="space-y-6" data-testid="cc-step2">
        <div><h3 className="font-heading text-2xl tracking-tight">RACE SETTINGS</h3><p className="text-race-sub text-sm">Configure how racers will compete.</p></div>

        <div className="bg-race-surface border border-race-line p-6" data-testid="cc-difficulty-presets">
          <div className="overline mb-3">Difficulty preset</div>
          <div className="grid grid-cols-3 gap-2">
            {[["easy", "Easy", "Short · forgiving"], ["medium", "Medium", "Balanced"], ["hard", "Hard", "Long · unforgiving"]].map(([k, label, desc]) => (
              <button key={k} type="button" onClick={() => applyDifficulty(k)} data-testid={`cc-difficulty-${k}`}
                className={`border rounded-md p-3 text-left transition-colors ${activeDifficulty === k ? "border-race-volt bg-race-volt/10" : "border-race-line hover:border-race-volt/60"}`}>
                <div className={`font-heading text-lg ${activeDifficulty === k ? "text-race-volt" : "text-race-txt"}`}>{label}</div>
                <div className="text-[11px] text-race-sub">{desc}</div>
                <div className="text-[10px] text-race-muted mt-1 font-mono">{DIFFICULTY_PRESETS[k].length_chars} chars · {DIFFICULTY_PRESETS[k].transmission} btns · {DIFFICULTY_PRESETS[k].penalty_seconds}s</div>
              </button>
            ))}
          </div>
          <p className="text-race-muted text-[11px] mt-2">Presets set length, transmission and penalty in one tap — fine-tune any value below.{activeDifficulty ? "" : " (Custom settings)"}</p>
        </div>

        <div className="bg-race-surface border border-race-line p-6">
          <div className="overline mb-3">Race Format</div>
          <div className="grid md:grid-cols-2 gap-4">
            <F label="Length (characters)"><input type="number" className={inp} value={form.length_chars} onChange={(e) => set("length_chars", e.target.value)} data-testid="cc-length" /></F>
            <div>
              <F label="Track Type"><select className={inp} value={form.track_type} onChange={(e) => set("track_type", e.target.value)} data-testid="cc-track">{Object.entries(TRACK_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select></F>
              <div className="mt-1 font-mono text-xs text-race-volt" data-testid="cc-track-example">Ex: {trackCfg.example}</div>
            </div>
            <F label="Transmission (buttons, 4–16)" required><input type="number" min="4" max="16" className={inp} value={form.transmission}
              onChange={(e) => set("transmission", e.target.value)}
              onBlur={() => set("transmission", Math.max(4, Math.min(16, parseInt(form.transmission, 10) || 4)))}
              data-testid="cc-transmission" /></F>
            <F label="Penalty (sec/error)"><select className={inp} value={form.penalty_seconds} onChange={(e) => set("penalty_seconds", e.target.value)} data-testid="cc-penalty">{[1, 2, 5].map((p) => <option key={p} value={p}>{p}s</option>)}</select></F>
          </div>
        </div>

        {/* Custom keyboard */}
        <div className="bg-race-surface border border-race-line p-6">
          <label className="flex items-center gap-2 cursor-pointer mb-1" data-testid="cc-usecustom">
            <input type="checkbox" checked={useCustom} onChange={(e) => setUseCustom(e.target.checked)} />
            <span className="overline">Characters Used - define the exact keyboard buttons (optional)</span>
          </label>
          <p className="text-race-sub text-xs mb-3">
            Transmission {transmission} = <span className="text-race-txt font-mono">www.</span> + {customSlots} custom {customSlots === 1 ? "button" : "buttons"} + <span className="text-race-txt font-mono">.com</span>.
            {" "}Buttons must be {trackCfg.label}{trackCfg.requireSpecial ? " and the set must include at least one special character" : ""}.
          </p>
          {useCustom && (
            <div className="mb-3 font-mono text-sm text-race-volt" data-testid="cc-char-count">Buttons: {transmission} (www. + {customSlots} custom + .com)</div>
          )}
          <div className="mt-1">
            <KeyboardPreview transmission={transmission} trackKey={form.track_type} editable={useCustom}
              values={chars} onChangeKey={(i, v) => { const cv = sanitizeForTrack(v, form.track_type); setChars((a) => a.map((x, j) => j === i ? cv : x)); }} validRe={trackCfg.re} />
          </div>
        </div>

        {/* Advanced settings (progressive disclosure) */}
        <details className="bg-race-surface border border-race-line p-6 group" data-testid="cc-advanced">
          <summary className="overline cursor-pointer select-none flex items-center justify-between">ADVANCED CONTEST SETTINGS <span className="text-race-sub text-xs">Expand ▼</span></summary>
          <div className="mt-4">
            {mode === "single" ? (
              <div>
                <label className="overline block mb-1">BR Protocol URL - Domain Estate <span className="text-race-muted normal-case tracking-normal text-[10px]">optional - auto-drawn if blank</span></label>
                <div className="flex gap-2">
                  <input className={inp} value={form.br_protocol_url} onChange={(e) => set("br_protocol_url", e.target.value)} placeholder="www.4thAve.io/TR#…" data-testid="cc-protocol" />
                  <button type="button" onClick={generateEstate} disabled={drawing} data-testid="cc-generate-estate"
                    className="shrink-0 bg-race-volt/15 border border-race-volt/50 text-race-volt hover:bg-race-volt hover:text-race-bg font-mono text-xs uppercase tracking-wide px-4 rounded-sm transition-colors disabled:opacity-50">
                    {drawing ? "…" : "Generate"}
                  </button>
                </div>
                <p className="text-race-muted text-xs mt-1">Click <span className="text-race-volt">Generate</span> to draw one from the IBRA repository, paste a URL from the 4th AVE Domain Estate Manager, or leave blank to auto-draw on publish.</p>
              </div>
            ) : (
              <div className="text-race-sub text-xs border border-dashed border-race-line rounded-sm p-3" data-testid="cc-series-estate-note">
                A separate Domain Estate is auto-drawn from the IBRA repository for each contest in the series.
              </div>
            )}
          </div>
        </details>
      </div>

      {/* STEP 3 — SPONSORSHIP & MEDIA */}
      <div style={{ display: step === 3 ? "block" : "none" }} className="space-y-6" data-testid="cc-step3">
        <div><h3 className="font-heading text-2xl tracking-tight">SPONSORSHIP & MEDIA</h3><p className="text-race-sub text-sm">Add optional sponsor information, event artwork and advertising placements.</p></div>
        <div className="bg-race-surface border border-race-line p-6">
          <div className="overline mb-3">Does this contest include sponsorship or advertising?</div>
          <div className="flex gap-2 flex-wrap">
            {[["no", "No Sponsor"], ["yes", "Add Sponsor"]].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setHasSponsor(k === "yes")} data-testid={`cc-sponsor-${k}`}
                className={`px-4 py-2 rounded-sm text-sm font-mono uppercase tracking-wide border transition-colors ${(hasSponsor ? "yes" : "no") === k ? "bg-race-red border-race-red text-race-bg" : "border-race-line text-race-sub hover:border-race-txt"}`}>{l}</button>
            ))}
          </div>
        </div>
        <div style={{ display: hasSponsor ? "block" : "none" }} className="bg-race-surface border border-race-line p-6">
          <h3 className="font-heading text-2xl tracking-tight mb-1">SPONSORSHIP & FLYER</h3>
          <p className="text-race-sub text-sm mb-4">Optional. If no flyer is uploaded, the Text Racing logo is used. Everything here can be edited later from the contest page.</p>
          <div className="grid md:grid-cols-2 gap-5">
            <Uploader label="Event Flyer" value={imgs.flyer_image_url} onChange={setImg("flyer_image_url")} testid="cc-flyer" />
            <AiAdGenerator format="flyer" onChange={setImg("flyer_image_url")} testid="cc-flyer" />
            <F label="Sponsor Name" optional><input className={inp} value={form.sponsor_name} onChange={(e) => set("sponsor_name", e.target.value)} data-testid="cc-sponsor" /></F>
            <F label="Sponsor Track URL (www.___.com on track)" optional><input className={inp} value={form.sponsor_track_url} onChange={(e) => set("sponsor_track_url", e.target.value)} placeholder="www.sponsor.com" data-testid="cc-track-url" /></F>
            <div className="md:col-span-2 border border-race-line rounded-md p-4 bg-race-elevated" data-testid="cc-prerace-block">
              <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                <span className="overline">Pre-Race Ad / Sponsorship (until T-1:30)</span>
                <div className="flex rounded-sm overflow-hidden border border-race-line" data-testid="cc-prerace-format">
                  {[["single", "Single 324×77"], ["double", "Double 324×132"]].map(([k, l]) => (
                    <button key={k} type="button" onClick={() => set("pre_race_ad_format", k)} data-testid={`cc-format-${k}`}
                      className={`px-3 py-1.5 text-xs font-mono transition-colors ${form.pre_race_ad_format === k ? "bg-race-red text-race-bg" : "text-race-sub hover:text-race-txt"}`}>{l}</button>
                  ))}
                </div>
              </div>
              <div className="mx-auto bg-race-bg border border-dashed border-race-line rounded-sm overflow-hidden mb-3"
                   style={{ aspectRatio: form.pre_race_ad_format === "double" ? "324 / 132" : "324 / 77", maxWidth: 324 }}>
                {imgs.pre_race_ad_url
                  ? <img src={imgs.pre_race_ad_url} alt="Pre-race ad preview" className="w-full h-full object-contain" data-testid="cc-prerace-preview" />
                  : <div className="w-full h-full flex items-center justify-center text-race-muted text-xs">{form.pre_race_ad_format === "double" ? "324 × 132" : "324 × 77"} banner</div>}
              </div>
              <Uploader label="Upload banner" value={imgs.pre_race_ad_url} onChange={setImg("pre_race_ad_url")} testid="cc-prerace" />
              <p className="text-race-muted text-[11px] mt-2">Upload at the exact banner size ({form.pre_race_ad_format === "double" ? "324×132" : "324×77"}) for the sharpest display on any phone. <span className="text-race-sub">Triple banner (324×209) is available on request for advertisers but is not shown Pre-Race / In-Race.</span></p>
              <AiAdGenerator format={form.pre_race_ad_format} onChange={setImg("pre_race_ad_url")} testid="cc-prerace" />
            </div>
            <div className="md:col-span-2 border border-race-line rounded-md p-4 bg-race-elevated" data-testid="cc-inrace-block">
              <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                <span className="overline">In-Race Ad / Sponsorship (from T-1:29, above keyboard)</span>
                <span className="text-xs font-mono text-race-sub border border-race-line rounded-sm px-3 py-1.5">Banner X1 324×77</span>
              </div>
              <div className="mx-auto bg-race-bg border border-dashed border-race-line rounded-sm overflow-hidden mb-3"
                   style={{ aspectRatio: "324 / 77", maxWidth: 324 }}>
                {imgs.in_race_ad_url
                  ? <img src={imgs.in_race_ad_url} alt="In-race ad preview" className="w-full h-full object-contain" data-testid="cc-inrace-preview" />
                  : <div className="w-full h-full flex items-center justify-center text-race-muted text-xs">324 × 77 banner</div>}
              </div>
              <Uploader label="Upload banner" value={imgs.in_race_ad_url} onChange={setImg("in_race_ad_url")} testid="cc-inrace" />
              <p className="text-race-muted text-[11px] mt-2">Upload at the exact banner size (324×77). Shown above the keyboard once the countdown hits T-1:29.</p>
              <AiAdGenerator format="single" onChange={setImg("in_race_ad_url")} testid="cc-inrace" />
            </div>
            <div className="md:col-span-2 border border-race-line rounded-md p-4 bg-race-elevated" data-testid="cc-postrace-block">
              <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                <span className="overline">Finish-Line Ad / Sponsorship (Results screen)</span>
                <div className="flex rounded-sm overflow-hidden border border-race-line" data-testid="cc-postrace-format">
                  {Object.entries(AD_DIMS).map(([k, d]) => (
                    <button key={k} type="button" onClick={() => set("post_race_ad_format", k)} data-testid={`cc-postformat-${k}`}
                      className={`px-3 py-1.5 text-xs font-mono transition-colors ${form.post_race_ad_format === k ? "bg-race-red text-race-bg" : "text-race-sub hover:text-race-txt"}`}>{d.label} {d.w}×{d.h}</button>
                  ))}
                </div>
              </div>
              <div className="mx-auto bg-race-bg border border-dashed border-race-line rounded-sm overflow-hidden mb-3"
                   style={{ aspectRatio: `${(AD_DIMS[form.post_race_ad_format] || AD_DIMS.single).w} / ${(AD_DIMS[form.post_race_ad_format] || AD_DIMS.single).h}`, maxWidth: (AD_DIMS[form.post_race_ad_format] || AD_DIMS.single).w }}>
                {imgs.post_race_ad_url
                  ? <img src={imgs.post_race_ad_url} alt="Finish-line ad preview" className="w-full h-full object-contain" data-testid="cc-postrace-preview" />
                  : <div className="w-full h-full flex items-center justify-center text-race-muted text-xs">{(AD_DIMS[form.post_race_ad_format] || AD_DIMS.single).w} × {(AD_DIMS[form.post_race_ad_format] || AD_DIMS.single).h} banner</div>}
              </div>
              <Uploader label="Upload banner" value={imgs.post_race_ad_url} onChange={setImg("post_race_ad_url")} testid="cc-postrace" />
              <AiAdGenerator format={form.post_race_ad_format} onChange={setImg("post_race_ad_url")} testid="cc-postrace" />
            </div>
          </div>
        </div>
      </div>

      {/* STEP 4 — REVIEW & PUBLISH */}
      <div style={{ display: step === 4 ? "block" : "none" }} className="space-y-4" data-testid="cc-step4">
        <div><h3 className="font-heading text-2xl tracking-tight">REVIEW & PUBLISH</h3><p className="text-race-sub text-sm">Verify everything before sanctioning the contest.</p></div>

        {/* C. Pre-flight checklist */}
        <div className={`border p-5 rounded-md ${hasHardFail ? "border-race-red/50 bg-race-red/5" : "border-race-volt/40 bg-race-volt/5"}`} data-testid="cc-preflight">
          <div className="flex items-center gap-2 mb-3">
            <ListChecks size={16} className={hasHardFail ? "text-race-red" : "text-race-volt"} />
            <span className="overline">Pre-flight checklist</span>
            <span className="text-[11px] text-race-sub ml-auto">{hasHardFail ? "Resolve the items in red before publishing" : "All clear — ready to sanction"}</span>
          </div>
          <div className="space-y-1.5">
            {preflight.map((c, i) => (
              <div key={i} className="flex items-start gap-2 text-sm" data-testid={`cc-check-${c.status}`}>
                {c.status === "pass" ? <Check size={15} className="text-race-volt mt-0.5 shrink-0" />
                  : c.status === "warn" ? <AlertTriangle size={15} className="text-amber-400 mt-0.5 shrink-0" />
                  : <X size={15} className="text-race-red mt-0.5 shrink-0" />}
                <span className={c.status === "fail" ? "text-race-red" : c.status === "warn" ? "text-amber-500" : "text-race-txt"}>
                  {c.label}{c.detail && <span className="text-race-muted"> — {c.detail}</span>}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-race-surface border border-race-line p-6">
          <div className="font-heading text-3xl tracking-tight mb-4" data-testid="cc-review-name">{form.name || "Untitled contest"}</div>
          {[
            ["CONTEST", 1, [["Race Type", form.race_type], ["Start", mode === "series" ? `${seriesDates.length} contests (series)` : (form.start_time ? new Date(form.start_time).toLocaleString() : "—")]]],
            ["RACE SETTINGS", 2, [["Length", `${form.length_chars} chars`], ["Track", trackCfg.label], ["Transmission", `${transmission} buttons`], ["Penalty", `${form.penalty_seconds}s / error`], ["Podium", `${form.podium_size} winner(s)`]]],
            ["PRIZE", 1, [["Type", prizeLabel], ["Value", prizeValue]]],
          ].map(([title, gstep, rows]) => (
            <div key={title} className="border-t border-race-line/60 py-3 first:border-t-0">
              <div className="flex items-center justify-between mb-2">
                <span className="overline">{title}</span>
                <button type="button" onClick={() => go(gstep)} data-testid={`cc-edit-${gstep}`} className="text-xs font-mono uppercase text-race-volt hover:underline">Edit</button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-sm">
                {rows.map(([k, v]) => <div key={k}><span className="text-race-sub">{k}: </span><span className="text-race-txt">{v}</span></div>)}
              </div>
            </div>
          ))}
          <div className="border-t border-race-line/60 py-3">
            <div className="flex items-center justify-between mb-2">
              <span className="overline">SPONSORSHIP & MEDIA</span>
              <button type="button" onClick={() => go(3)} data-testid="cc-edit-3" className="text-xs font-mono uppercase text-race-volt hover:underline">Edit</button>
            </div>
            {hasSponsor ? (
              <div className="text-sm space-y-1">
                {form.sponsor_name && <div><span className="text-race-sub">Sponsor: </span>{form.sponsor_name}</div>}
                {[["Event Flyer", imgs.flyer_image_url], ["Pre-Race Ad", imgs.pre_race_ad_url], ["In-Race Ad", imgs.in_race_ad_url], ["Finish-Line Ad", imgs.post_race_ad_url]].map(([l, v]) => (
                  <div key={l} className={v ? "text-race-volt" : "text-race-muted"}>{v ? "✓" : "○"} {l}</div>
                ))}
              </div>
            ) : <div className="text-race-sub text-sm">No sponsor / ads for this contest.</div>}
          </div>
          {mode === "single" && (
            <div className="border-t border-race-line/60 py-3" data-testid="cc-review-estate">
              <div className="overline mb-2">4th AVE Domain Estate</div>
              {form.br_protocol_url ? (
                <div className="text-sm"><span className="text-race-sub">Selected: </span><span className="font-mono text-race-txt break-all">{form.br_protocol_url}</span></div>
              ) : estatePreview?.auto_mint ? (
                <div className="text-sm text-race-volt">A fresh Domain Estate will be minted and auto-drawn from the IBRA repository on publish.</div>
              ) : estatePreview?.url ? (
                <div className="text-sm">
                  <span className="text-race-sub">Auto-draw on publish: </span>
                  <span className="font-mono text-race-txt break-all">{estatePreview.url}</span>
                  <div className="text-race-muted text-[11px] mt-1">Next available from the IBRA pool ({estatePreview.available} in stock). The exact estate is reserved at publish.</div>
                </div>
              ) : (
                <div className="text-sm text-race-sub">A Domain Estate will be auto-drawn from the IBRA pool on publish.</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Sticky action bar */}
      <div className="sticky bottom-0 z-10 mt-6 -mx-1 px-1 bg-race-surface/95 backdrop-blur border-t border-race-line py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="cc-actionbar">
        <div className="flex items-center gap-3 flex-wrap">
          {step > 1 && <button type="button" onClick={() => go(step - 1)} data-testid="cc-back" className="border border-race-line hover:border-race-txt text-race-txt px-5 py-2.5 rounded-sm font-mono text-sm uppercase tracking-wide transition-colors">← Back</button>}
          <button type="button" onClick={saveDraft} data-testid="cc-save-draft" className="border border-race-line hover:border-race-txt text-race-sub hover:text-race-txt px-5 py-2.5 rounded-sm font-mono text-sm uppercase tracking-wide transition-colors">Save Draft</button>
          <span className="font-mono text-[11px] text-race-muted" data-testid="cc-draft-status">
            {dirty ? "Unsaved changes" : lastSavedAt ? "Saved just now" : ""}
            {lastSavedAt && !dirty && <button type="button" onClick={discardDraft} data-testid="cc-discard-draft" className="ml-3 underline hover:text-race-red transition-colors">Discard</button>}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {step < 4 ? (
            <button type="button" onClick={next} data-testid="cc-continue" className="bg-white hover:bg-race-txt text-race-bg font-mono text-sm uppercase tracking-wide px-6 py-2.5 rounded-sm transition-colors">Continue →</button>
          ) : (
            <button onClick={submit} disabled={busy || hasHardFail} data-testid="cc-submit"
              title={hasHardFail ? "Resolve the red checklist items first" : undefined}
              className="bg-white hover:bg-race-txt disabled:opacity-50 disabled:cursor-not-allowed text-race-bg font-heading text-xl tracking-wide px-6 py-2.5 rounded-sm transition-colors">
              {busy ? "…" : "SANCTION & PUBLISH CONTEST"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ReviewQueue() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/review-queue").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const resolve = async (id, action) => {
    try { await api.post(`/admin/review-queue/${id}/resolve?action=${action}`); toast.success(`Result ${action}d`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div className="mt-4 bg-race-surface border border-race-line" data-testid="review-queue">
      {rows.length === 0 && <p className="p-4 text-race-sub text-sm">No flagged results. Anti-cheat queue is clear.</p>}
      {rows.map((r) => (
        <div key={r.id} className="flex items-center justify-between px-4 py-3 border-b border-race-line/60 last:border-0">
          <div className="font-mono text-sm">
            <div className="text-race-txt">{r.race_type} · {r.cps} cps · {r.final_time}s</div>
            <div className="text-race-red text-xs">{(r.flags || []).join(", ")}</div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => resolve(r.id, "approve")} className="text-xs border border-race-volt text-race-volt px-3 py-1.5 rounded-sm">Approve</button>
            <button onClick={() => resolve(r.id, "void")} className="text-xs border border-race-red text-race-red px-3 py-1.5 rounded-sm">Void</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function ContentEngine() {
  const [form, setForm] = useState({ race_type: "trivia", category: "general", difficulty: "medium", count: 8 });
  const [busy, setBusy] = useState(false);
  const gen = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/content/generate", { ...form, count: +form.count }); toast.success(`Generated ${data.generated} items via LLM`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-4 bg-race-surface border border-race-line p-6 max-w-lg" data-testid="content-engine">
      <p className="text-race-sub text-sm mb-4">Expand the practice & contest content bank with LLM-generated trivia and sprint phrases (Emergent universal key).</p>
      <div className="grid grid-cols-2 gap-4 mb-4">
        <F label="Type"><select className={inp} value={form.race_type} onChange={(e) => setForm({ ...form, race_type: e.target.value })} data-testid="ce-type"><option value="trivia">Trivia</option><option value="sprint">Sprint</option></select></F>
        <F label="Difficulty"><select className={inp} value={form.difficulty} onChange={(e) => setForm({ ...form, difficulty: e.target.value })}><option>easy</option><option>medium</option><option>hard</option></select></F>
        <F label="Category / Theme"><input className={inp} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="ce-category" /></F>
        <F label="Count"><input type="number" className={inp} value={form.count} onChange={(e) => setForm({ ...form, count: e.target.value })} /></F>
      </div>
      <button onClick={gen} disabled={busy} data-testid="ce-generate"
        className="w-full bg-race-red hover:bg-race-redhover disabled:opacity-50 text-race-bg font-semibold py-3 rounded-sm transition-colors">
        {busy ? "Generating…" : "Generate Content"}
      </button>
    </div>
  );
}

function AuditLog() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/admin/audit").then((r) => setLogs(r.data)).catch(() => {}); }, []);
  return (
    <div className="mt-4 bg-race-surface border border-race-line max-h-[60vh] overflow-auto" data-testid="audit-log">
      {logs.map((l, i) => (
        <div key={i} className="flex items-center justify-between px-4 py-2 border-b border-race-line/60 last:border-0 font-mono text-xs">
          <span className="text-race-txt">{l.action} <span className="text-race-muted">· {l.category}</span></span>
          <span className="text-race-sub">{l.actor_email || "system"} · {new Date(l.created_at).toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

function UsersList() {
  const [users, setUsers] = useState([]);
  const [keep, setKeep] = useState({});
  const load = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggleKeep = (email) => setKeep((k) => ({ ...k, [email]: !k[email] }));

  const delOne = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This removes the account and its race history.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Account deleted"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const grant = async (u) => {
    try { await api.post(`/admin/users/${u.id}/grant-license`); toast.success(`${u.name} is now licensed to race`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const setPass = async (u) => {
    const pw = window.prompt(`Set a new password for ${u.email} (min 6 chars):`);
    if (!pw) return;
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    try { await api.post(`/admin/users/${u.id}/set-password`, { password: pw }); toast.success(`Password updated for ${u.email}`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const setRole = async (u, role) => {
    const adding = role === "admin";
    if (!window.confirm(`${adding ? "Grant admin to" : "Remove admin from"} ${u.email}? They stay a member either way.`)) return;
    try { await api.post(`/admin/users/${u.id}/role`, { role }); toast.success(adding ? `${u.name} is now an admin (and still a member)` : `Admin removed from ${u.name}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const purge = async () => {
    const keepList = Object.keys(keep).filter((e) => keep[e]);
    if (!window.confirm(`Delete ALL contests, races, licenses, flagged results, and every member EXCEPT the ${keepList.length} marked "keep"? Admins are always kept.`)) return;
    if (!window.confirm("This permanently removes all test data and cannot be undone. Proceed?")) return;
    try {
      const { data } = await api.post("/admin/purge-test-data", { keep_emails: keepList });
      toast.success(`Removed ${data.members_deleted} test accounts + all mock data`);
      setKeep({});
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="mt-4" data-testid="users-list">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <p className="text-race-sub text-sm">Mark your real accounts as <span className="text-race-txt font-semibold">keep</span>, then purge the rest. Admins are always kept.</p>
        <button onClick={purge} data-testid="purge-test-btn"
          className="border border-race-red text-race-red hover:bg-race-red hover:text-race-bg font-heading text-lg tracking-wide px-4 py-2 rounded-sm transition-colors">
          PURGE TEST DATA
        </button>
      </div>
      <div className="bg-race-surface border border-race-line max-h-[60vh] overflow-auto">
        {users.map((u) => {
          const roles = u.roles && u.roles.length ? u.roles : [u.role];
          const isAdmin = roles.includes("admin");
          return (
          <div key={u.id} className="flex items-center justify-between px-4 py-2.5 border-b border-race-line/60 last:border-0 text-sm" data-testid={`user-row-${u.email}`}>
            <span className="text-race-txt">{u.name} <span className="text-race-muted font-mono text-xs">{u.email}</span></span>
            <span className="flex items-center gap-4 font-mono text-xs text-race-sub">
              <span className="flex items-center gap-1.5" data-testid={`roles-${u.email}`}>
                <span className="px-1.5 py-0.5 rounded-sm bg-race-line/60 text-race-txt uppercase tracking-wide text-[10px]">member</span>
                {isAdmin && <span className="px-1.5 py-0.5 rounded-sm bg-race-red/20 text-race-red uppercase tracking-wide text-[10px]">admin</span>}
              </span>
              <span className="text-race-volt">{u.avg_cps ?? u.skill_cps} cps</span>
              {isAdmin ? (
                <button onClick={() => setRole(u, "member")} data-testid={`demote-${u.email}`}
                  className="text-race-muted hover:text-race-red transition-colors">remove admin</button>
              ) : (
                <button onClick={() => setRole(u, "admin")} data-testid={`promote-${u.email}`}
                  className="text-race-muted hover:text-race-red transition-colors">make admin</button>
              )}
              <button onClick={() => grant(u)} data-testid={`lic-${u.email}`}
                className={u.has_license ? "text-race-volt" : "text-race-muted hover:text-race-volt transition-colors"}>
                {u.has_license ? "licensed ✓" : "grant license"}
              </button>
              <button onClick={() => setPass(u)} data-testid={`pw-${u.email}`}
                className="text-race-muted hover:text-race-info transition-colors">set password</button>
              {!isAdmin && (
                <>
                  <label className="flex items-center gap-1 cursor-pointer text-race-txt">
                    <input type="checkbox" checked={!!keep[u.email]} onChange={() => toggleKeep(u.email)} data-testid={`keep-${u.email}`} /> keep
                  </label>
                  <button onClick={() => delOne(u)} data-testid={`del-${u.email}`} className="text-race-muted hover:text-race-red transition-colors"><Trash2 size={14} /></button>
                </>
              )}
            </span>
          </div>
          );
        })}
      </div>
    </div>
  );
}

function Team() {
  const [admins, setAdmins] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/admins").then((r) => setAdmins(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name || !form.email || !form.password) return toast.error("Fill in name, email and a password.");
    if (form.password.length < 6) return toast.error("Password must be at least 6 characters.");
    setBusy(true);
    try {
      await api.post("/admin/admins", form);
      toast.success(`${form.email} can now sign in as an admin`);
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const demote = async (id, email) => {
    if (!window.confirm(`Remove admin access from ${email}?`)) return;
    try { await api.post(`/admin/admins/${id}/demote`); toast.success("Admin access removed"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="mt-4 grid gap-6 md:grid-cols-2" data-testid="team-panel">
      <div className="bg-race-surface border border-race-line p-6" data-testid="create-admin-form">
        <h3 className="font-heading text-2xl tracking-tight mb-1">ADD A TEAM ADMIN</h3>
        <p className="text-race-sub text-sm mb-4">Create a new IBRA admin, or promote an existing member by using their email.</p>
        <div className="grid gap-4">
          <F label="Full Name"><input className={inp} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-name" /></F>
          <F label="Email"><input type="email" className={inp} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-email" /></F>
          <F label="Temporary Password"><input className={inp} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="admin-password" placeholder="Share this with the team member" /></F>
        </div>
        <button onClick={create} disabled={busy} data-testid="admin-create-btn"
          className="w-full mt-6 bg-race-red hover:bg-race-redhover disabled:opacity-50 text-race-bg font-heading text-2xl tracking-wide py-3 rounded-sm transition-colors">
          {busy ? "…" : "CREATE ADMIN"}
        </button>
      </div>
      <div className="bg-race-surface border border-race-line" data-testid="admins-list">
        <div className="px-4 py-3 overline border-b border-race-line/60">Current Admins ({admins.length})</div>
        {admins.map((a) => (
          <div key={a.id} className="flex items-center justify-between px-4 py-3 border-b border-race-line/60 last:border-0 text-sm" data-testid={`admin-row-${a.email}`}>
            <span className="text-race-txt">{a.name} <span className="text-race-muted font-mono text-xs block">{a.email}</span></span>
            <button onClick={() => demote(a.id, a.email)} data-testid={`admin-demote-${a.email}`}
              className="flex items-center gap-1 text-xs border border-race-line hover:border-race-red hover:text-race-red text-race-sub px-3 py-1.5 rounded-sm transition-colors">
              <Trash2 size={12} /> Remove
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function useContestView(rows) {
  const [view, setView] = useState("current");
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({ status: "", type: "", sponsor: "", from: "", to: "" });
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 30000); return () => clearInterval(t); }, []);
  let wizard = null;
  try { wizard = JSON.parse(localStorage.getItem(CC_DRAFT_KEY) || "null"); } catch {}
  const tagged = rows.map((c) => ({ ...c, _badge: badgeOf(c, now) }));
  const counts = { current: 0, previous: 0, drafts: wizard ? 1 : 0, all: tagged.length };
  tagged.forEach((c) => { counts[VIEW_OF[c._badge]] += 1; });
  const needle = q.trim().toLowerCase();
  const ts = (c) => new Date(c.start_time || 0).getTime();
  let list = tagged.filter((c) => (view === "all" || VIEW_OF[c._badge] === view)
    && (!needle || [c.name, c.sponsor_name, c.id, c.br_protocol_url, c.race_type].some((v) => (v || "").toLowerCase().includes(needle))));
  if (view === "all") {
    const f = filters;
    list = list.filter((c) => (!f.status || c._badge === f.status) && (!f.type || c.race_type === f.type)
      && (!f.sponsor || c.sponsor_name === f.sponsor)
      && (!f.from || (c.start_time && dayjs(c.start_time).format("YYYY-MM-DD") >= f.from))
      && (!f.to || (c.start_time && dayjs(c.start_time).format("YYYY-MM-DD") <= f.to)));
  }
  if (view === "current") list.sort((a, b) => (a._badge === "live" ? 0 : 1) - (b._badge === "live" ? 0 : 1) || ts(a) - ts(b));
  else if (view === "previous") list.sort((a, b) => new Date(b.completed_at || b.start_time || 0) - new Date(a.completed_at || a.start_time || 0));
  else if (view === "drafts") list.sort((a, b) => ts(a) - ts(b));
  else list.sort((a, b) => ts(b) - ts(a));
  const uniq = (k) => [...new Set(rows.map((c) => c[k]).filter(Boolean))].sort();
  return { view, setView, q, setQ, filters, setFilters, counts, list, now, wizard: view === "drafts" && !needle ? wizard : null,
    raceTypes: uniq("race_type"), sponsors: uniq("sponsor_name") };
}

const EMPTY_MSG = { current: "No live or upcoming races. Create one in the Create Contest tab.", previous: "No previous races yet.",
  drafts: "No drafts - every race is either scheduled or finished.", all: "No races match these filters." };

function ContestManager({ onGoCreate }) {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);   // contest id being edited
  const [cancelling, setCancelling] = useState(null); // contest id being cancelled
  const [cancelNote, setCancelNote] = useState("");
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [editCustom, setEditCustom] = useState(false);
  const [editChars, setEditChars] = useState([]);

  const editTx = Math.max(4, Math.min(16, parseInt(form.transmission, 10) || 4));
  const editSlots = editTx - 2;
  const editTrackCfg = TRACK_TYPES[form.track_type] || TRACK_TYPES.alphabetical;
  useEffect(() => {
    if (!editCustom) return;
    setEditChars((a) => {
      const next = a.slice(0, editSlots);
      while (next.length < editSlots) next.push("");
      return next;
    });
  }, [editSlots, editCustom]);

  const load = () => api.get("/contests").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toLocalInput = (iso) => iso || "";

  const startEdit = (c) => {
    setEditing(c.id);
    const cc = Array.isArray(c.custom_characters) ? c.custom_characters : [];
    setEditCustom(cc.length > 0);
    setEditChars(cc.length ? [...cc] : [""]);
    setForm({
      name: c.name || "", start_time: toLocalInput(c.start_time),
      entry_fee_credits: c.entry_fee_credits ?? 0, prize_pool_credits: c.prize_pool_credits ?? 0,
      prize_type: c.prize_type || "cash", prize_fmv_usd: c.prize_fmv_usd ?? "",
      prize_btc_amount: c.prize_btc_amount ?? "", web3_partner: c.web3_partner || "",
      prize_rate_lock: c.prize_rate_lock ?? true,
      prize_description: c.prize_description || "", prize_image_url: c.prize_image_url || "",
      difficulty: c.difficulty || "", sponsor_name: c.sponsor_name || "",
      length_chars: c.length_chars ?? 20, transmission: c.transmission ?? 8,
      track_type: c.track_type || "alphabetical",
      flyer_image_url: c.flyer_image_url || null,
      in_race_ad_url: c.in_race_ad_url || null,
      pre_race_ad_url: c.pre_race_ad_url || null,
      pre_race_ad_format: c.pre_race_ad_format || "single",
      post_race_ad_url: c.post_race_ad_url || null,
      post_race_ad_format: c.post_race_ad_format || "single",
      reminder_lead_default: c.reminder_lead_default || 3,
    });
  };

  const save = async (id) => {
    if (editCustom) {
      const filled = editChars.map((c) => c.trim());
      if (filled.length !== editSlots || filled.some((c) => !c))
        return toast.error(`Fill all ${editSlots} custom buttons - www. and .com are automatic`);
      for (const tok of filled)
        if (!editTrackCfg.re.test(tok)) return toast.error(`"${tok}" isn't valid for a ${editTrackCfg.label} track`);
      if (form.track_type === "alphanumeric" && !alnumHasBoth(filled))
        return toast.error("An alphanumeric track needs at least one letter AND one number across the buttons");
      if (editTrackCfg.requireSpecial && ![...filled.join("")].some((ch) => SPECIALS.includes(ch)))
        return toast.error("A special-characters track must include at least one special character");
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name, difficulty: form.difficulty || null, sponsor_name: form.sponsor_name || null,
        entry_fee_credits: +form.entry_fee_credits, prize_pool_credits: +form.prize_pool_credits,
        prize_type: form.prize_type || "cash",
        prize_fmv_usd: form.prize_type !== "cash" ? (+form.prize_fmv_usd || 0) : null,
        prize_btc_amount: form.prize_type === "bitcoin" ? (+form.prize_btc_amount || 0) : null,
        web3_partner: form.prize_type === "bitcoin" ? (form.web3_partner || null) : null,
        prize_rate_lock: form.prize_type === "bitcoin" ? !!form.prize_rate_lock : null,
        rate_relock: form.prize_type === "bitcoin" && form.prize_rate_lock ? !!form.rate_relock : false,
        rate_relock_lead_hours: Number(form.rate_relock_lead_hours || 0),
        prize_description: (form.prize_type === "gift_card" || form.prize_type === "physical") ? (form.prize_description || "") : "",
        prize_image_url: (form.prize_type === "gift_card" || form.prize_type === "physical") ? (form.prize_image_url || "") : "",
        start_time: form.start_time ? new Date(form.start_time).toISOString() : undefined,
        length_chars: +form.length_chars, transmission: editTx, track_type: form.track_type,
        custom_characters: editCustom ? editChars.map((c) => c.trim()).filter(Boolean) : [],
        flyer_image_url: form.flyer_image_url || null,
        in_race_ad_url: form.in_race_ad_url || null,
        pre_race_ad_url: form.pre_race_ad_url || null,
        pre_race_ad_format: form.pre_race_ad_format || "single",
        post_race_ad_url: form.post_race_ad_url || null,
        post_race_ad_format: form.post_race_ad_format || "single",
        reminder_lead_default: +form.reminder_lead_default || 3,
      };
      await api.patch(`/contests/${id}`, payload);
      if (rows.find((r) => r.id === id)?.status === "hold") {
        const { data: rl } = await api.post(`/admin/race-logic/enforce/${id}?phase=scheduled`);
        if (rl.status === "blocked") toast.error(`Saved, but still on hold: ${rl.blockers.join("; ")}`);
        else toast.success("Contest updated - readiness check passed, released from HOLD");
      } else toast.success("Contest updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const cancel = async (c) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/contests/${c.id}/cancel`, { note: cancelNote.trim() || null });
      toast.success(`Contest cancelled - ${data.notified} registrant(s) notified, ${data.refunded_registrants} refunded`);
      setCancelling(null); setCancelNote("");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const startCancel = (c) => { setEditing(null); setCancelNote(""); setCancelling(cancelling === c.id ? null : c.id); };
  const duplicate = (c) => {
    try { localStorage.setItem(CC_DRAFT_KEY, JSON.stringify(buildDraftFromContest(c))); } catch {}
    toast.success(`Cloned "${c.name}" - finish the copy in Create Contest`);
    onGoCreate?.();
  };

  const cv = useContestView(rows);
  const [selected, setSelected] = useState(new Set());
  const [focusField, setFocusField] = useState(null);
  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSel = cv.list.length > 0 && cv.list.every((c) => selected.has(c.id));
  const toggleAll = () => setSelected((s) => { const n = new Set(s); cv.list.forEach((c) => (allSel ? n.delete(c.id) : n.add(c.id))); return n; });
  useEffect(() => { setSelected(new Set()); }, [cv.view]);
  useEffect(() => {
    if (!editing || !focusField) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="cm-editform-${editing}"] [data-testid="${focusField}"]`);
      if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.classList.add("ring-2", "ring-amber-500"); setTimeout(() => el.classList.remove("ring-2", "ring-amber-500"), 2500); el.focus?.(); }
      setFocusField(null);
    }, 120);
    return () => clearTimeout(t);
  }, [editing, focusField]);
  const fixHold = (c, field) => { if (editing !== c.id) startEdit(c); setCancelling(null); setFocusField(field); };
  const fmt = (iso) => iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "-";

  return (
    <div className="mt-4 space-y-3" data-testid="contest-manager">
      <ContestToolbar view={cv.view} setView={cv.setView} counts={cv.counts} q={cv.q} setQ={cv.setQ}
        filters={cv.filters} setFilters={cv.setFilters} raceTypes={cv.raceTypes} sponsors={cv.sponsors} />
      <p className="text-race-muted text-xs">Edit scheduled contests or cancel one - cancelling refunds every registrant's entry fee and frees the Domain Estate back into the pool.</p>
      {cv.wizard && (
        <div className="bg-race-surface border border-dashed border-amber-400/40 flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3" data-testid="cm-wizard-draft">
          <div className="min-w-0">
            <div className="flex items-center gap-2"><span className="font-heading text-lg tracking-tight truncate">{cv.wizard.form?.name || "Untitled contest"}</span><StatusBadge kind="draft" /></div>
            <div className="text-race-sub text-xs mt-0.5">Unpublished setup saved in this browser{cv.wizard.step != null ? ` • stopped at step ${Number(cv.wizard.step) + 1} of 4` : ""}</div>
          </div>
          <button onClick={() => onGoCreate?.()} data-testid="cm-wizard-continue"
            className="flex items-center gap-1 px-3 py-2 rounded-sm border border-amber-400/50 text-amber-500 hover:bg-amber-400/10 text-xs font-mono transition-colors shrink-0">
            <Pencil size={13} /> Continue setup
          </button>
        </div>
      )}
      <BulkBar selected={selected} rows={rows} onClear={() => setSelected(new Set())} onDone={() => { setSelected(new Set()); load(); }} />
      {cv.list.length > 0 && (
        <label className="flex items-center gap-2 text-xs font-mono text-race-sub cursor-pointer w-fit" data-testid="cm-select-all-row">
          <input type="checkbox" checked={allSel} onChange={toggleAll} className="accent-current" data-testid="cm-select-all" /> Select all in this tab ({cv.list.length})
        </label>
      )}
      {cv.list.length === 0 && !cv.wizard && <div className="bg-race-surface border border-race-line p-6 text-race-sub text-sm" data-testid="cm-empty">{rows.length === 0 ? "No contests yet. Create one in the Create Contest tab." : cv.q ? "No races match your search." : EMPTY_MSG[cv.view]}</div>}
      {cv.list.map((c) => (
        <div key={c.id} className={`bg-race-surface border ${c._badge === "live" ? "border-red-500/50 border-l-2 border-l-red-500" : "border-race-line"}`} data-testid={`cm-row-${c.id}`}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3">
            <div className="min-w-0 flex items-start gap-3">
              <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleSel(c.id)} aria-label={`Select ${c.name}`}
                className="mt-1.5 shrink-0 accent-current cursor-pointer" data-testid={`cm-select-${c.id}`} />
              <div className="min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-heading text-lg tracking-tight truncate">{c.name}</span>
                <StatusBadge kind={c._badge} testid={`cm-badge-${c.id}`} />
                {c.status === "hold" && c._badge === "draft" && <span className="font-mono text-[9px] uppercase tracking-widest text-race-muted shrink-0">on hold · readiness</span>}
              </div>
              <div className="text-race-sub text-xs mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                <span className="text-race-txt">{fmt(c.start_time)}</span>
                {(c._badge === "upcoming" || c._badge === "live" || c._badge === "draft") && relTime(c.start_time, cv.now) && (
                  <span className={c._badge === "live" ? "text-red-500 font-semibold" : "text-sky-500"} data-testid={`cm-rel-${c.id}`}>{relTime(c.start_time, cv.now)}</span>
                )}
                <span>• {c.registrant_count} participant{c.registrant_count === 1 ? "" : "s"}</span>
                {c.sponsor_name && <span>• Sponsor: <span className="text-race-txt">{c.sponsor_name}</span></span>}
                <span>• {c.race_type}</span>
                <span className="text-race-muted">• prize {c.prize_pool_credits}cr • fee {c.entry_fee_credits}cr</span>
              </div>
              <div className="font-mono text-[10px] text-race-muted truncate mt-0.5">{c.br_protocol_url}</div>
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => duplicate(c)} data-testid={`cm-duplicate-${c.id}`}
                className="flex items-center gap-1 px-3 py-2 rounded-sm border border-race-line text-race-sub hover:text-race-txt hover:border-race-txt text-xs font-mono transition-colors">
                <Copy size={13} /> Duplicate
              </button>
              <button onClick={() => (editing === c.id ? setEditing(null) : startEdit(c))} data-testid={`cm-edit-${c.id}`}
                disabled={["cancelled", "refunded", "completed"].includes(c.status)}
                className="flex items-center gap-1 px-3 py-2 rounded-sm border border-race-line text-race-sub hover:text-race-txt hover:border-race-txt text-xs font-mono transition-colors disabled:opacity-40">
                <Pencil size={13} /> Edit
              </button>
              <button onClick={() => startCancel(c)} data-testid={`cm-cancel-${c.id}`}
                disabled={["cancelled", "refunded", "completed"].includes(c.status)}
                className="flex items-center gap-1 px-3 py-2 rounded-sm border border-race-red/50 text-race-red hover:bg-race-red hover:text-race-bg text-xs font-mono transition-colors disabled:opacity-40">
                <Ban size={13} /> Cancel
              </button>
            </div>
          </div>
          {c.status === "hold" && <HoldReasons c={c} onFix={fixHold} onDuplicate={duplicate} onRechecked={load} />}
          {cancelling === c.id && (
            <div className="border-t border-race-red/40 bg-race-red/5 p-4" data-testid={`cm-cancelform-${c.id}`}>
              <label className="overline block mb-1 text-race-red">Cancellation note (optional - shown to registrants)</label>
              <textarea className={inp + " min-h-[70px]"} value={cancelNote} onChange={(e) => setCancelNote(e.target.value)}
                placeholder="e.g. Postponed due to sponsor scheduling. New date announced soon." data-testid="cm-cancel-note" />
              <p className="text-race-sub text-xs mt-2">Confirming refunds every registrant's entry fee, emails them, posts an in-app notice, and frees the Domain Estate back to Un-Used.</p>
              <div className="flex gap-2 mt-3">
                <button onClick={() => cancel(c)} disabled={busy} data-testid={`cm-confirm-cancel-${c.id}`}
                  className="bg-race-red hover:bg-race-redhover text-race-bg font-mono text-xs uppercase tracking-wide px-6 py-2.5 rounded-sm transition-colors disabled:opacity-50">{busy ? "Cancelling…" : "Confirm Cancellation"}</button>
                <button onClick={() => setCancelling(null)} className="border border-race-line text-race-sub hover:text-race-txt px-4 py-2.5 rounded-sm text-xs font-mono">Keep Contest</button>
              </div>
            </div>
          )}
          {editing === c.id && (
            <div className="border-t border-race-line p-4 grid md:grid-cols-2 gap-3" data-testid={`cm-editform-${c.id}`}>
              <F label="Contest Name"><input className={inp} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cm-f-name" /></F>
              <F label="Start Time"><DateTimePicker value={form.start_time} onChange={(iso) => setForm({ ...form, start_time: iso })} testid="cm-f-start" /></F>
              <F label="Entry Fee (credits)"><input type="number" className={inp} value={form.entry_fee_credits} onChange={(e) => setForm({ ...form, entry_fee_credits: e.target.value })} data-testid="cm-f-entry" /></F>
              <F label="Prize Type"><select className={inp} value={form.prize_type} onChange={(e) => setForm({ ...form, prize_type: e.target.value })} data-testid="cm-f-prize-type">
                <option value="cash">Cash (credits)</option>
                <option value="bitcoin">Bitcoin</option>
                <option value="gift_card">Gift Card</option>
                <option value="physical">Physical Item</option>
              </select></F>
              {form.prize_type === "cash" ? (
                <F label="Prize Pool (credits)"><input type="number" className={inp} value={form.prize_pool_credits} onChange={(e) => setForm({ ...form, prize_pool_credits: e.target.value })} data-testid="cm-f-prize" /></F>
              ) : (
                <F label={form.prize_type === "bitcoin" ? "Bitcoin Prize (USD value)" : "Prize Value (USD FMV)"}><input type="number" min="0" className={inp} value={form.prize_fmv_usd} onChange={(e) => setForm({ ...form, prize_fmv_usd: e.target.value })} placeholder="e.g. 500" data-testid="cm-f-prize-fmv" /></F>
              )}
              {form.prize_type === "bitcoin" && (
                <F label="Web3 / KYC Partner"><select className={inp} value={form.web3_partner || ""} onChange={(e) => setForm({ ...form, web3_partner: e.target.value })} data-testid="cm-f-web3-partner">
                  <option value="">Select a partner…</option>
                  {["Coinbase", "Circle", "Fireblocks", "Persona"].map((p) => <option key={p} value={p}>{p}</option>)}
                </select></F>
              )}
              {form.prize_type === "bitcoin" && (
                <label className="md:col-span-2 flex items-center gap-2 text-xs text-race-txt cursor-pointer" data-testid="cm-f-rate-lock-row">
                  <input type="checkbox" checked={!!form.prize_rate_lock} onChange={(e) => setForm({ ...form, prize_rate_lock: e.target.checked })} data-testid="cm-f-rate-lock" className="accent-[#F7931A]" />
                  Lock USD ↔ BTC rate at publish (fixes the BTC payout)
                </label>
              )}
              {(form.prize_type === "gift_card" || form.prize_type === "physical") && (
                <div className="md:col-span-2">
                  <label className="overline block mb-1">Prize Description</label>
                  <input className={inp} value={form.prize_description} onChange={(e) => setForm({ ...form, prize_description: e.target.value })}
                    placeholder={form.prize_type === "gift_card" ? "e.g. $100 Amazon gift card" : "e.g. PlayStation 5"} data-testid="cm-f-prize-desc" />
                  <div className="mt-3">
                    <Uploader label="Prize photo (shown to the winner)" value={form.prize_image_url} onChange={(url) => setForm({ ...form, prize_image_url: url || "" })} testid="cm-f-prize-image" />
                  </div>
                </div>
              )}
              <F label="Difficulty"><input className={inp} value={form.difficulty} onChange={(e) => setForm({ ...form, difficulty: e.target.value })} placeholder="easy / medium / hard" data-testid="cm-f-difficulty" /></F>
              <F label="Sponsor Name"><input className={inp} value={form.sponsor_name} onChange={(e) => setForm({ ...form, sponsor_name: e.target.value })} data-testid="cm-f-sponsor" /></F>
              <F label="Length (characters)"><input type="number" className={inp} value={form.length_chars} onChange={(e) => setForm({ ...form, length_chars: e.target.value })} data-testid="cm-f-length" /></F>
              <F label="Reminder before start"><select className={inp} value={form.reminder_lead_default} onChange={(e) => setForm({ ...form, reminder_lead_default: e.target.value })} data-testid="cm-f-reminder">{[3, 5, 10].map((m) => <option key={m} value={m}>{m} min</option>)}</select></F>
              <F label="Transmission (buttons, 4–16)"><input type="number" min="4" max="16" className={inp} value={form.transmission}
                onChange={(e) => setForm({ ...form, transmission: e.target.value })}
                onBlur={() => setForm({ ...form, transmission: Math.max(4, Math.min(16, parseInt(form.transmission, 10) || 4)) })}
                data-testid="cm-f-transmission" /></F>
              <div className="md:col-span-2">
                <F label="Track Type"><select className={inp} value={form.track_type} onChange={(e) => setForm({ ...form, track_type: e.target.value })} data-testid="cm-f-track">{Object.entries(TRACK_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select></F>
              </div>
              <div className="md:col-span-2 border border-race-line rounded-md p-4 bg-race-elevated">
                <label className="flex items-center gap-2 cursor-pointer mb-1" data-testid="cm-usecustom">
                  <input type="checkbox" checked={editCustom} onChange={(e) => setEditCustom(e.target.checked)} />
                  <span className="overline">Characters Used - define the exact keyboard buttons</span>
                </label>
                <p className="text-race-sub text-xs mb-3">Transmission {editTx} = <span className="text-race-txt font-mono">www.</span> + {editSlots} custom + <span className="text-race-txt font-mono">.com</span>. Buttons must be {editTrackCfg.label}{editTrackCfg.requireSpecial ? " incl. ≥1 special char" : ""}.</p>
                {editCustom && (
                  <div className="mb-3 font-mono text-xs text-race-volt">Buttons: {editTx} (www. + {editSlots} custom + .com)</div>
                )}
                <KeyboardPreview transmission={editTx} trackKey={form.track_type} editable={editCustom}
                  values={editChars} onChangeKey={(i, v) => { const cv = sanitizeForTrack(v, form.track_type); setEditChars((a) => a.map((x, j) => j === i ? cv : x)); }} validRe={editTrackCfg.re} />
              </div>
              <div className="md:col-span-2 border border-race-line rounded-md p-4 bg-race-elevated" data-testid="cm-flyer-block">
                <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                  <span className="overline">Event Flyer - Calendar Promo Image</span>
                  <span className="text-xs font-mono text-race-sub border border-race-line rounded-sm px-3 py-1.5">Portrait 1024×1280</span>
                </div>
                <div className="mx-auto bg-race-bg border border-dashed border-race-line rounded-sm overflow-hidden mb-3"
                     style={{ aspectRatio: "1024 / 1280", maxWidth: 176 }}>
                  {form.flyer_image_url
                    ? <img src={form.flyer_image_url} alt="Event flyer" className="w-full h-full object-cover" data-testid="cm-flyer-preview" />
                    : <div className="w-full h-full flex items-center justify-center text-race-muted text-xs text-center px-2">Flyer shown on the calendar</div>}
                </div>
                <Uploader label="Replace flyer" value={form.flyer_image_url} onChange={(v) => setForm({ ...form, flyer_image_url: v })} testid="cm-flyer" />
                <p className="text-race-muted text-[11px] mt-2">This is the promotional image shown for the race on the main League Calendar.</p>
                <AiAdGenerator format="flyer" onChange={(v) => setForm({ ...form, flyer_image_url: v })} testid="cm-flyer" />
              </div>
              <div className="md:col-span-2 border border-race-line rounded-md p-4 bg-race-elevated" data-testid="cm-inrace-block">
                <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                  <span className="overline">In-Race Ad / Sponsorship (above keyboard)</span>
                  <span className="text-xs font-mono text-race-sub border border-race-line rounded-sm px-3 py-1.5">Banner X1 324×77</span>
                </div>
                <div className="mx-auto bg-race-bg border border-dashed border-race-line rounded-sm overflow-hidden mb-3"
                     style={{ aspectRatio: "324 / 77", maxWidth: 324 }}>
                  {form.in_race_ad_url
                    ? <img src={form.in_race_ad_url} alt="In-race ad" className="w-full h-full object-contain" data-testid="cm-inrace-preview" />
                    : <div className="w-full h-full flex items-center justify-center text-race-muted text-xs">324 × 77 banner</div>}
                </div>
                <Uploader label="Replace banner" value={form.in_race_ad_url} onChange={(v) => setForm({ ...form, in_race_ad_url: v })} testid="cm-inrace" />
                <AiAdGenerator format="single" onChange={(v) => setForm({ ...form, in_race_ad_url: v })} testid="cm-inrace" />
              </div>
              <div className="md:col-span-2 border border-race-line rounded-md p-4 bg-race-elevated">
                <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                  <span className="overline">Pre-Race Ad / Sponsorship</span>
                  <div className="flex rounded-sm overflow-hidden border border-race-line">
                    {[["single", "Single 324×77"], ["double", "Double 324×132"]].map(([k, l]) => (
                      <button key={k} type="button" onClick={() => setForm({ ...form, pre_race_ad_format: k })} data-testid={`cm-format-${k}`}
                        className={`px-3 py-1.5 text-xs font-mono transition-colors ${form.pre_race_ad_format === k ? "bg-race-red text-race-bg" : "text-race-sub hover:text-race-txt"}`}>{l}</button>
                    ))}
                  </div>
                </div>
                <div className="mx-auto bg-race-bg border border-dashed border-race-line rounded-sm overflow-hidden mb-3"
                     style={{ aspectRatio: form.pre_race_ad_format === "double" ? "324 / 132" : "324 / 77", maxWidth: 324 }}>
                  {form.pre_race_ad_url
                    ? <img src={form.pre_race_ad_url} alt="Pre-race ad" className="w-full h-full object-contain" data-testid="cm-prerace-preview" />
                    : <div className="w-full h-full flex items-center justify-center text-race-muted text-xs">{form.pre_race_ad_format === "double" ? "324 × 132" : "324 × 77"} banner</div>}
                </div>
                <Uploader label="Replace banner" value={form.pre_race_ad_url} onChange={(v) => setForm({ ...form, pre_race_ad_url: v })} testid="cm-prerace" />
                <AiAdGenerator format={form.pre_race_ad_format} onChange={(v) => setForm({ ...form, pre_race_ad_url: v })} testid="cm-prerace" />
              </div>
              <div className="md:col-span-2 border border-race-line rounded-md p-4 bg-race-elevated">
                <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                  <span className="overline">Finish-Line Ad (Results screen)</span>
                  <div className="flex rounded-sm overflow-hidden border border-race-line">
                    {Object.entries(AD_DIMS).map(([k, d]) => (
                      <button key={k} type="button" onClick={() => setForm({ ...form, post_race_ad_format: k })} data-testid={`cm-postformat-${k}`}
                        className={`px-3 py-1.5 text-xs font-mono transition-colors ${form.post_race_ad_format === k ? "bg-race-red text-race-bg" : "text-race-sub hover:text-race-txt"}`}>{d.label} {d.w}×{d.h}</button>
                    ))}
                  </div>
                </div>
                <div className="mx-auto bg-race-bg border border-dashed border-race-line rounded-sm overflow-hidden mb-3"
                     style={{ aspectRatio: `${(AD_DIMS[form.post_race_ad_format] || AD_DIMS.single).w} / ${(AD_DIMS[form.post_race_ad_format] || AD_DIMS.single).h}`, maxWidth: (AD_DIMS[form.post_race_ad_format] || AD_DIMS.single).w }}>
                  {form.post_race_ad_url
                    ? <img src={form.post_race_ad_url} alt="Finish-line ad" className="w-full h-full object-contain" data-testid="cm-postrace-preview" />
                    : <div className="w-full h-full flex items-center justify-center text-race-muted text-xs">{(AD_DIMS[form.post_race_ad_format] || AD_DIMS.single).w} × {(AD_DIMS[form.post_race_ad_format] || AD_DIMS.single).h} banner</div>}
                </div>
                <Uploader label="Replace banner" value={form.post_race_ad_url} onChange={(v) => setForm({ ...form, post_race_ad_url: v })} testid="cm-postrace" />
                <AiAdGenerator format={form.post_race_ad_format} onChange={(v) => setForm({ ...form, post_race_ad_url: v })} testid="cm-postrace" />
              </div>
              <div className="md:col-span-2 flex gap-2">
                <button onClick={() => save(c.id)} disabled={busy} data-testid={`cm-save-${c.id}`}
                  className="bg-race-red hover:bg-race-redhover text-race-bg font-mono text-xs uppercase tracking-wide px-6 py-2.5 rounded-sm transition-colors disabled:opacity-50">{busy ? "Saving…" : "Save Changes"}</button>
                <button onClick={() => setEditing(null)} className="border border-race-line text-race-sub hover:text-race-txt px-4 py-2.5 rounded-sm text-xs font-mono">Close</button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function EstateManager({ onUseInContest }) {
  const [filter, setFilter] = useState("unused");
  const [page, setPage] = useState(0);
  const [data, setData] = useState({ counts: {}, estates: [], total_matching: 0 });
  const [selected, setSelected] = useState(null);
  const [copied, setCopied] = useState(null);
  const PAGE = 25;

  const load = () => api.get(`/4thave/estates?prefix=TR&filter=${filter}&skip=${page * PAGE}&limit=${PAGE}`)
    .then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, page]);
  useEffect(() => { setPage(0); }, [filter]);

  const copy = async (url) => {
    try { await navigator.clipboard.writeText(url); setCopied(url); toast.success("Estate URL copied"); setTimeout(() => setCopied(null), 1500); }
    catch { toast.error("Could not copy"); }
  };

  const release = async (estateId) => {
    if (!window.confirm("Release this reserved Domain Estate back to Un-Used? Do this only if its draft contest was abandoned.")) return;
    try {
      await api.post(`/4thave/estates/${estateId}/release`);
      toast.success("Estate released back to Un-Used");
      setSelected(null);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const counts = data.counts || {};
  const totalPages = Math.max(1, Math.ceil((data.total_matching || 0) / PAGE));
  const statusColor = { available: "text-race-volt", reserved: "text-sky-400", activated: "text-green-400", voided: "text-race-sub" };

  return (
    <div className="bg-race-surface border border-race-line p-6" data-testid="estate-manager">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <h3 className="font-heading text-2xl tracking-tight">DOMAIN ESTATE MANAGER</h3>
        <div className="flex rounded-sm overflow-hidden border border-race-line" data-testid="estate-filter">
          {[["unused", "Un-Used"], ["used", "Used"], ["both", "Both"]].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`estate-filter-${k}`}
              className={`px-4 py-2 text-xs font-mono uppercase tracking-wide transition-colors ${filter === k ? "bg-race-red text-race-bg" : "text-race-sub hover:text-race-txt"}`}>{l}</button>
          ))}
        </div>
      </div>

      {/* Ledger summary */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        {[["unused", "Un-Used (Left)"], ["used", "Used"], ["total", "Total Owned"]].map(([k, l]) => (
          <div key={k} className="bg-race-elevated border border-race-line rounded-sm py-3 text-center" data-testid={`estate-ledger-${k}`}>
            <div className={`tabular text-3xl ${k === "unused" ? (counts.unused < 5 ? "text-race-red" : "text-race-volt") : "text-race-txt"}`}>{counts[k] ?? 0}</div>
            <div className="overline text-[9px] mt-1">{l}</div>
          </div>
        ))}
      </div>
      {counts.unused < 5 && <p className="text-race-red text-xs mb-3">Low inventory - purchase a new batch below to keep sanctioning contests.</p>}

      {/* Selected estate */}
      {selected && (
        <div className="mb-4 bg-race-bg border border-race-volt/50 rounded-sm p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="estate-selected">
          <div className="min-w-0">
            <div className="overline text-[9px] text-race-volt mb-1">Selected Domain Estate</div>
            <div className="font-mono text-xs text-race-txt truncate">{selected.url}</div>
          </div>
          <div className="flex gap-2 shrink-0">
            <button onClick={() => copy(selected.url)} data-testid="estate-selected-copy"
              className="flex items-center gap-1 px-3 py-2 rounded-sm border border-race-line hover:border-race-red text-race-sub hover:text-race-red text-xs font-mono transition-colors">
              {copied === selected.url ? <Check size={13} /> : <Copy size={13} />} Copy
            </button>
            {(selected.status === "reserved" || selected.status === "voided") && (
              <button onClick={() => release(selected.estate_id)} data-testid="estate-release"
                className="flex items-center gap-1 px-3 py-2 rounded-sm border border-race-volt/50 text-race-volt hover:bg-race-volt hover:text-race-bg text-xs font-mono transition-colors">
                <RotateCcw size={13} /> Release
              </button>
            )}
            {selected.status === "available" && (
              <button onClick={() => onUseInContest?.(selected.url)} data-testid="estate-use-in-contest"
                className="bg-race-red hover:bg-race-redhover text-race-bg font-mono text-xs uppercase tracking-wide px-4 py-2 rounded-sm transition-colors">
                Use in Create Contest
              </button>
            )}
          </div>
        </div>
      )}

      {/* List */}
      <div className="border border-race-line rounded-sm divide-y divide-race-line" data-testid="estate-list">
        {data.estates.length === 0 && <div className="p-4 text-race-sub text-sm" data-testid="estate-empty">No estates match this filter.</div>}
        {data.estates.map((e) => (
          <button key={e.estate_id} onClick={() => setSelected(e)} data-testid={`estate-row-${e.estate_id}`}
            className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left transition-colors ${selected?.estate_id === e.estate_id ? "bg-race-volt/10" : "hover:bg-race-elevated"}`}>
            <div className="min-w-0">
              <div className="font-mono text-xs text-race-txt truncate">{e.url}</div>
              {e.event_reference && <div className="text-[10px] text-race-sub truncate">{e.event_reference}</div>}
            </div>
            <span className={`shrink-0 overline text-[9px] ${statusColor[e.status] || "text-race-sub"}`}>{e.status === "available" ? "un-used" : e.status}</span>
          </button>
        ))}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-3">
        <span className="text-race-sub text-xs" data-testid="estate-page-info">
          {data.total_matching > 0 ? `${page * PAGE + 1}–${Math.min((page + 1) * PAGE, data.total_matching)} of ${data.total_matching}` : "0 results"}
        </span>
        <div className="flex gap-2">
          <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} data-testid="estate-prev"
            className="px-3 py-1.5 rounded-sm border border-race-line text-race-sub text-xs disabled:opacity-40 hover:text-race-txt">Prev</button>
          <span className="text-race-sub text-xs self-center">Page {page + 1}/{totalPages}</span>
          <button onClick={() => setPage((p) => (p + 1 < totalPages ? p + 1 : p))} disabled={page + 1 >= totalPages} data-testid="estate-next"
            className="px-3 py-1.5 rounded-sm border border-race-line text-race-sub text-xs disabled:opacity-40 hover:text-race-txt">Next</button>
        </div>
      </div>
    </div>
  );
}

function FourthAve({ onUseInContest }) {
  const [clients, setClients] = useState([]);
  const [prefix, setPrefix] = useState("TR");
  const [qty, setQty] = useState(1000);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/4thave/clients").then((r) => setClients(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const buy = async () => {
    const n = +qty;
    if (!n || n < 1000) return toast.error("Minimum purchase is 1,000 estates ($1.00)");
    setBusy(true);
    try {
      const { data } = await api.post("/payments/estates/checkout", { client_prefix: prefix, quantity: n, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setBusy(false); }
  };

  return (
    <div className="mt-4 space-y-6" data-testid="fourthave-panel">
      <p className="text-race-sub text-sm">4th AVE issues pre-purchased Domain Estates under <span className="text-race-txt font-mono">www.4thAve.io</span>. IBRA draws one automatically each time a contest is created. Estates cost <span className="text-race-txt">$0.001</span> each - purchased in batches of at least 1,000 ($1.00 minimum).</p>
      <div className="grid gap-4 md:grid-cols-2">
        {clients.map((c) => (
          <div key={c.prefix} className="bg-race-surface border border-race-line p-5" data-testid={`fa-client-${c.prefix}`}>
            <div className="flex items-baseline justify-between mb-3">
              <h3 className="font-heading text-2xl tracking-tight">{c.name}</h3>
              <span className="font-mono text-xs text-race-sub">www.4thAve.io/{c.prefix}#…</span>
            </div>
            <div className="grid grid-cols-4 gap-2 text-center">
              {[["available", "Available"], ["reserved", "Reserved"], ["activated", "Activated"], ["voided", "Voided"]].map(([k, l]) => (
                <div key={k} className="bg-race-elevated border border-race-line rounded-sm py-2">
                  <div className={`tabular text-2xl ${k === "available" && c.counts[k] < 100 ? "text-race-red" : "text-race-volt"}`}>{c.counts[k]}</div>
                  <div className="overline text-[9px]">{l}</div>
                </div>
              ))}
            </div>
            {c.counts.available < 100 && <p className="text-race-red text-xs mt-3">Low inventory - purchase a new batch.</p>}
          </div>
        ))}
      </div>

      <EstateManager onUseInContest={onUseInContest} />

      <div className="bg-race-surface border border-race-line p-6">
        <h3 className="font-heading text-2xl tracking-tight mb-4">PURCHASE BATCH (via Stripe)</h3>
        <div className="grid md:grid-cols-3 gap-4 items-end">
          <F label="Client"><select className={inp} value={prefix} onChange={(e) => setPrefix(e.target.value)} data-testid="fa-client-select">{clients.map((c) => <option key={c.prefix} value={c.prefix}>{c.name} ({c.prefix})</option>)}</select></F>
          <F label="Quantity (min 1,000)"><input type="number" min="1000" step="1000" className={inp} value={qty} onChange={(e) => setQty(e.target.value)} data-testid="fa-qty" /></F>
          <div className="text-race-sub text-sm">Cost: <span className="text-race-txt font-mono">${(Math.max(0, +qty || 0) * 0.001).toFixed(2)}</span> + Stripe fees</div>
        </div>
        <button onClick={buy} disabled={busy} data-testid="fa-generate"
          className="w-full mt-5 bg-race-red hover:bg-race-redhover disabled:opacity-50 text-race-bg font-heading text-2xl tracking-wide py-3 rounded-sm transition-colors">
          {busy ? "…" : "PURCHASE ESTATES"}
        </button>
        <p className="text-race-muted text-xs mt-2">Secure checkout by Stripe. Estates are minted into inventory once payment is confirmed.</p>
      </div>
    </div>
  );
}

function PrizeFunding() {
  const [contests, setContests] = useState([]);
  const [contestId, setContestId] = useState("");
  const [credits, setCredits] = useState(5000);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/contests").then((r) => { setContests(r.data); if (r.data[0]) setContestId(r.data[0].id); }).catch(() => {});
  }, []);

  const fund = async () => {
    if (!contestId) return toast.error("Select a contest");
    const n = +credits;
    if (!n || n < 50) return toast.error("Minimum funding is 50 credits ($0.50)");
    setBusy(true);
    try {
      const { data } = await api.post("/payments/prize-fund/checkout", { contest_id: contestId, credits: n, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setBusy(false); }
  };

  return (
    <div className="mt-4 space-y-6" data-testid="prize-funding-panel">
      <p className="text-race-sub text-sm">Fund a contest's prize pool with real money via Stripe. 1 credit = $0.01. Funded credits are added to the contest's advertised prize pool.</p>
      <div className="bg-race-surface border border-race-line p-6">
        <h3 className="font-heading text-2xl tracking-tight mb-4">FUND PRIZE POOL</h3>
        <div className="grid md:grid-cols-3 gap-4 items-end">
          <F label="Contest"><select className={inp} value={contestId} onChange={(e) => setContestId(e.target.value)} data-testid="pf-contest">
            {contests.length === 0 && <option value="">No contests yet</option>}
            {contests.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select></F>
          <F label="Credits to add"><input type="number" min="50" step="50" className={inp} value={credits} onChange={(e) => setCredits(e.target.value)} data-testid="pf-credits" /></F>
          <div className="text-race-sub text-sm">Charge: <span className="text-race-txt font-mono">${(Math.max(0, +credits || 0) / 100).toFixed(2)}</span></div>
        </div>
        <button onClick={fund} disabled={busy || !contestId} data-testid="pf-fund"
          className="w-full mt-5 bg-race-red hover:bg-race-redhover disabled:opacity-50 text-race-bg font-heading text-2xl tracking-wide py-3 rounded-sm transition-colors">
          {busy ? "…" : "FUND VIA STRIPE"}
        </button>
      </div>
    </div>
  );
}


function AffiliateAdmin() {
  const [overview, setOverview] = useState(null);
  const [config, setConfig] = useState(null);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [savingCfg, setSavingCfg] = useState(false);

  const loadOverview = () => api.get("/admin/affiliate/overview").then((r) => setOverview(r.data)).catch(() => {});
  const loadConfig = () => api.get("/admin/affiliate/config").then((r) => setConfig(r.data)).catch(() => {});
  const loadRows = () => api.get(`/admin/affiliate/commissions${filter ? `?status=${filter}` : ""}`).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { loadOverview(); loadConfig(); }, []);
  useEffect(() => { loadRows(); /* eslint-disable-next-line */ }, [filter]);

  const setTier = (i, key, val) => setConfig((c) => ({ ...c, tiers: c.tiers.map((t, j) => j === i ? { ...t, [key]: val } : t) }));

  const saveConfig = async () => {
    setSavingCfg(true);
    try {
      const payload = {
        tiers: config.tiers.map((t) => ({
          name: t.name, min: +t.min, max: t.max === "" || t.max === null ? null : +t.max, rate: +t.rate,
        })),
        eligible: config.eligible || "all",
        min_prize_credits: +config.min_prize_credits || 1,
      };
      await api.put("/admin/affiliate/config", payload);
      toast.success("Affiliate config saved");
      loadConfig();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSavingCfg(false); }
  };

  const act = async (id, action) => {
    try {
      await api.post(`/admin/affiliate/commissions/${id}/${action}`);
      toast.success(`Commission ${action}d`);
      loadRows(); loadOverview();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const cr = (c) => (c || 0).toLocaleString();
  const usd = (c) => `$${((c || 0) / 100).toFixed(2)}`;
  const badge = { pending: "text-race-volt", approved: "text-sky-400", paid: "text-green-400", reversed: "text-race-sub" };

  return (
    <div className="mt-4 space-y-6" data-testid="affiliate-admin-panel">
      <p className="text-race-sub text-sm">Racing Team commissions are funded separately as a marketing expense - <span className="text-race-txt">never deducted from a winner's prize</span>. Rates are locked historically per commission.</p>

      {/* Overview */}
      {overview && (
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4" data-testid="affiliate-overview">
          {["pending", "approved", "paid", "reversed"].map((st) => (
            <div key={st} className="bg-race-surface border border-race-line p-4" data-testid={`aff-ov-${st}`}>
              <div className="overline mb-1">{st}</div>
              <div className={`font-heading text-2xl ${badge[st]}`}>{cr(overview.by_status[st].total)}</div>
              <div className="text-race-sub text-xs">{usd(overview.by_status[st].total)} • {overview.by_status[st].count} items</div>
            </div>
          ))}
        </div>
      )}

      {/* Tier config */}
      {config && (
        <div className="bg-race-surface border border-race-line p-6" data-testid="affiliate-config">
          <h3 className="font-heading text-2xl tracking-tight mb-4">TIERS & RULES</h3>
          <div className="space-y-3">
            {config.tiers.map((t, i) => (
              <div key={i} className="grid grid-cols-4 gap-3 items-end" data-testid={`aff-tier-${i}`}>
                <F label="Name"><input className={inp} value={t.name} onChange={(e) => setTier(i, "name", e.target.value)} /></F>
                <F label="Min racers"><input type="number" className={inp} value={t.min} onChange={(e) => setTier(i, "min", e.target.value)} data-testid={`aff-tier-min-${i}`} /></F>
                <F label="Max (blank = ∞)"><input type="number" className={inp} value={t.max ?? ""} onChange={(e) => setTier(i, "max", e.target.value)} data-testid={`aff-tier-max-${i}`} /></F>
                <F label="Rate (0–1)"><input type="number" step="0.01" className={inp} value={t.rate} onChange={(e) => setTier(i, "rate", e.target.value)} data-testid={`aff-tier-rate-${i}`} /></F>
              </div>
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-3 mt-4">
            <F label="Eligible contests"><select className={inp} value={config.eligible} onChange={(e) => setConfig({ ...config, eligible: e.target.value })}><option value="all">All scheduled contests</option><option value="none">None (paused)</option></select></F>
            <F label="Min prize (credits) to earn"><input type="number" className={inp} value={config.min_prize_credits} onChange={(e) => setConfig({ ...config, min_prize_credits: e.target.value })} /></F>
          </div>
          <button onClick={saveConfig} disabled={savingCfg} data-testid="aff-save-config"
            className="mt-5 bg-race-red hover:bg-race-redhover disabled:opacity-50 text-race-bg font-mono text-xs uppercase tracking-wide px-6 py-3 rounded-sm transition-colors">
            {savingCfg ? "Saving…" : "Save Config"}
          </button>
        </div>
      )}

      {/* Ledger */}
      <div className="bg-race-surface border border-race-line p-6" data-testid="affiliate-ledger">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <h3 className="font-heading text-2xl tracking-tight">COMMISSION LEDGER</h3>
          <select className={inp + " w-auto"} value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="aff-filter">
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="paid">Paid</option>
            <option value="reversed">Reversed</option>
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left overline text-race-sub border-b border-race-line">
                <th className="py-2 pr-3">Affiliate</th><th className="pr-3">Winner</th><th className="pr-3">Contest</th>
                <th className="pr-3">Tier</th><th className="pr-3">Amount</th><th className="pr-3">Status</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan="7" className="py-4 text-race-sub" data-testid="aff-ledger-empty">No commissions for this filter.</td></tr>}
              {rows.map((c) => (
                <tr key={c.id} className="border-b border-race-line/50" data-testid={`aff-row-${c.id}`}>
                  <td className="py-2 pr-3 text-race-txt">{c.affiliate_name}</td>
                  <td className="pr-3 text-race-sub">{c.referred_name}</td>
                  <td className="pr-3 text-race-sub truncate max-w-[160px]">{c.contest_name}</td>
                  <td className="pr-3 text-race-sub">{c.tier_name} ({Math.round((c.rate || 0) * 100)}%)</td>
                  <td className="pr-3 font-mono text-race-volt">{cr(c.commission_amount)} <span className="text-race-muted text-xs">({usd(c.commission_amount)})</span></td>
                  <td className={`pr-3 uppercase text-xs font-mono ${badge[c.status]}`}>{c.status}</td>
                  <td className="py-2">
                    <div className="flex gap-1.5">
                      {c.status === "pending" && <button onClick={() => act(c.id, "approve")} data-testid={`aff-approve-${c.id}`} className="px-2 py-1 rounded-sm bg-sky-500/15 border border-sky-400/40 text-sky-500 text-xs hover:bg-sky-500/25">Approve</button>}
                      {c.status === "approved" && <button onClick={() => act(c.id, "pay")} data-testid={`aff-pay-${c.id}`} className="px-2 py-1 rounded-sm bg-green-500/15 border border-green-400/40 text-green-300 text-xs hover:bg-green-500/25">Pay</button>}
                      {["pending", "approved"].includes(c.status) && <button onClick={() => act(c.id, "reverse")} data-testid={`aff-reverse-${c.id}`} className="px-2 py-1 rounded-sm border border-race-line text-race-sub text-xs hover:text-race-red hover:border-race-red">Reverse</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function RestrictedRegions() {
  const [cfg, setCfg] = useState({ region_codes: [], country_codes: [], block_unresolved: false });
  const [regions, setRegions] = useState("");
  const [countries, setCountries] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/restricted-regions").then((r) => {
      setCfg(r.data);
      setRegions((r.data.region_codes || []).join(", "));
      setCountries((r.data.country_codes || []).join(", "));
    }).catch(() => {});
  }, []);

  const save = async () => {
    const parse = (s) => s.split(",").map((x) => x.trim().toUpperCase()).filter(Boolean);
    setBusy(true);
    try {
      const { data } = await api.put("/admin/restricted-regions", {
        region_codes: parse(regions), country_codes: parse(countries),
        block_unresolved: cfg.block_unresolved,
      });
      setCfg(data);
      setRegions((data.region_codes || []).join(", "));
      setCountries((data.country_codes || []).join(", "));
      toast.success("Restricted regions saved");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-4 bg-race-surface border border-race-line p-6 max-w-2xl" data-testid="restricted-regions">
      <p className="text-race-sub text-sm mb-4">
        Members located in these regions are blocked from registering for real-money contests (entry fee or prize pool &gt; 0). Free practice stays open everywhere.
      </p>
      <div className="grid gap-4">
        <F label="Blocked US State Codes (comma-separated, e.g. WA, ID, MT)">
          <input className={inp} value={regions} onChange={(e) => setRegions(e.target.value)} data-testid="rr-regions" placeholder="WA, ID, MT" />
        </F>
        <F label="Blocked Country Codes (comma-separated, e.g. FR, DE)">
          <input className={inp} value={countries} onChange={(e) => setCountries(e.target.value)} data-testid="rr-countries" placeholder="FR, DE" />
        </F>
        <label className="flex items-center gap-2 text-sm text-race-sub cursor-pointer" data-testid="rr-block-unresolved">
          <input type="checkbox" checked={cfg.block_unresolved}
            onChange={(e) => setCfg({ ...cfg, block_unresolved: e.target.checked })} />
          Block members whose location can't be determined
        </label>
      </div>
      <button onClick={save} disabled={busy} data-testid="rr-save"
        className="w-full mt-6 bg-race-red hover:bg-race-redhover disabled:opacity-50 text-race-bg font-heading text-2xl tracking-wide py-3 rounded-sm transition-colors">
        {busy ? "…" : "SAVE RESTRICTED REGIONS"}
      </button>
    </div>
  );
}
